import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  Img,
} from 'remotion';

// 设计基准尺寸：所有模板内部坐标都基于 1280×720 布局，
// 外层用 useVideoConfig 实际尺寸等比缩放以适配任意合成宽高。
const BASE_W = 1280;
const BASE_H = 720;

// 统一的内容缩放舞台：保证所有模板在不同合成尺寸下等比居中显示、
// 不裁切、不拉伸（取最小缩放比）。
const ScaledStage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { width, height } = useVideoConfig();
  const scale = Math.min(width / BASE_W, height / BASE_H);
  const contentW = BASE_W * scale;
  const contentH = BASE_H * scale;
  const left = (width - contentW) / 2;
  const top = (height - contentH) / 2;
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: BASE_W,
        height: BASE_H,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
    >
      {children}
    </div>
  );
};

// =====================================================================
// 图片模板（新） · 2026-04-18
// ---------------------------------------------------------------------
// 本文件包含多个风格各异、视觉新颖的图片展示模板，ID 均以 Image 前缀：
//   · ImageOrigamiUnfold          —— 和风折纸依次展开，图片从折痕中"绽放"
//   · ImageNeonArcade             —— 赛博朋克霓虹街机柜群，CRT 弯曲 / 扫描线
//   · ImageCinemaScopeTrailer     —— 2.39:1 宽银幕预告，Ken Burns + 胶片色调
//   · ImageCrystalPrism           —— 悬浮旋转水晶立方，六面图片 + 星云折射
//   · ImageHolographicMuseum      —— 未来全息展厅，5 屏碾压 + 透视网格地板
//   · ImageLiquidChromeGallery    —— 液态铬画廊，巨幅金属镜面 + 涟漪切换 + 彩虹油膜
//   · ImageAuroraRibbonGallery    —— 极光丝带画廊，极地夜空下巨幅画框 + 流动极光丝带
//   · ImageVolumetricStageSpotlight — 体积光剧场，聚光灯柱 + 烟雾 + 反光地板
//
// 温馨 / 可爱 系列（2026-04-18 · V4）：
//   · ImagePastelDreamBubbles     —— 马卡龙梦境泡泡，柔软浮动 + 彩虹星星点缀
//   · ImageHandmadeScrapbook      —— 手作剪贴簿，彩色胶带 / 贴纸 / 手写涂鸦
//   · ImageCozyWindowSnowfall     —— 温馨雪窗台，木窗花 + 飘雪 + 热可可 + 小熊
// =====================================================================

// 统一默认示例图 —— 8 张常用横图（picsum seed）
const DEFAULT_IMAGES_8 = [
  'https://picsum.photos/seed/aurora-01/900/900',
  'https://picsum.photos/seed/aurora-02/900/900',
  'https://picsum.photos/seed/aurora-03/900/900',
  'https://picsum.photos/seed/aurora-04/900/900',
  'https://picsum.photos/seed/aurora-05/900/900',
  'https://picsum.photos/seed/aurora-06/900/900',
  'https://picsum.photos/seed/aurora-07/900/900',
  'https://picsum.photos/seed/aurora-08/900/900',
];

// 小工具：可重复的伪随机
const rand = (seed: number) => {
  const v = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return v - Math.floor(v);
};

// ---------------------------------------------------------------------
// #1  ImageOrigamiUnfold —— 和风折纸展开
// 特点：
// · 6 张方形折纸从天而降，带阴影落在和纸桌面
// · 每张依次"展开"（scale + rotateY 阶段动画），露出中间的图片
// · 背景为米色和纸（washi），表面有纤维纹理
// · 标题竖排假名 / 汉字 · 底部印章
// ---------------------------------------------------------------------
export const ImageOrigamiUnfold: React.FC<{
  images?: string[];
  labels?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  labels = ['春', '夏', '秋', '冬', '月', '花'],
  title = 'ORIGAMI · 折',
  subtitle = '六帖 · 光阴折叠为页',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 桌面参数
  const cardN = 6;
  const cardSize = Math.min(width, height) * 0.24;
  const startY = height * 0.32;

  // 6 个位置（两排 × 三列）
  const positions = Array.from({ length: cardN }).map((_, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const gapX = cardSize * 1.38;
    const gapY = cardSize * 1.42;
    return {
      x: width / 2 + (col - 1) * gapX,
      y: startY + row * gapY,
    };
  });

  const headerOp = interpolate(frame, [0, 24], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 生成 washi 纤维纹理（用伪随机的小线条）
  const fibers = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; len: number; ang: number; s: number }> = [];
    for (let i = 0; i < 180; i++) {
      arr.push({
        x: rand(i * 7.7) * width,
        y: rand(i * 3.3) * height,
        len: 8 + rand(i * 1.9) * 40,
        ang: rand(i * 5.1) * 180,
        s: rand(i * 2.2),
      });
    }
    return arr;
  }, [width, height]);

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 55%, #efe4cb 0%, #e2d2b0 55%, #b39872 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 桌面纹理（和纸纤维） */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {fibers.map((f, i) => (
          <line
            key={`fi-${i}`}
            x1={f.x}
            y1={f.y}
            x2={f.x + Math.cos((f.ang * Math.PI) / 180) * f.len}
            y2={f.y + Math.sin((f.ang * Math.PI) / 180) * f.len}
            stroke={`rgba(150,110,70,${0.05 + f.s * 0.1})`}
            strokeWidth={0.7}
          />
        ))}
      </svg>

      {/* 左侧竖排毛笔标题 */}
      <div
        style={{
          position: 'absolute',
          left: 52,
          top: 70,
          opacity: headerOp,
          writingMode: 'vertical-rl',
          fontFamily: '"STKaiti", "KaiTi", "Songti SC", "Georgia", serif',
          fontSize: 42,
          lineHeight: 1.2,
          color: '#3a1c12',
          letterSpacing: 12,
          textShadow: '1px 2px 0 rgba(255,240,210,0.6)',
        }}
      >
        {title}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 118,
          top: 80,
          opacity: headerOp * 0.8,
          writingMode: 'vertical-rl',
          fontFamily: '"STKaiti", "KaiTi", "Songti SC", serif',
          fontSize: 15,
          letterSpacing: 8,
          color: '#6b3c22',
        }}
      >
        {subtitle}
      </div>

      {/* 右上角朱印 */}
      <div
        style={{
          position: 'absolute',
          right: 70,
          top: 60,
          width: 72,
          height: 72,
          borderRadius: 8,
          background:
            'linear-gradient(145deg, #b5321c 0%, #7d1f10 100%)',
          border: '3px solid #a0291a',
          boxShadow:
            '0 6px 14px rgba(80,20,10,0.4), inset 0 0 0 2px rgba(255,255,255,0.15)',
          color: '#ffe8c9',
          fontFamily: '"STKaiti", "KaiTi", serif',
          fontSize: 26,
          fontWeight: 700,
          display: 'grid',
          placeItems: 'center',
          letterSpacing: 2,
          lineHeight: 1,
          opacity: headerOp,
          transform: `rotate(-4deg)`,
        }}
      >
        折
        <br />
        光
      </div>

      {/* 6 张折纸 */}
      {positions.map((pos, i) => {
        const dropDelay = 6 + i * 5;
        const unfoldDelay = 38 + i * 16;

        // 下落 spring
        const drop = spring({
          frame: frame - dropDelay,
          fps,
          config: { damping: 14, stiffness: 130, mass: 0.8 },
        });
        const dropY = interpolate(drop, [0, 1], [-220, 0]);
        const dropOp = interpolate(frame - dropDelay, [0, 14], [0, 1], {
          extrapolateRight: 'clamp',
        });
        const wobble = Math.sin((frame - dropDelay) / 6) * (1 - drop) * 6;

        // 展开进度 0..1：横向剪切 → 竖向再展开
        const unfoldH = interpolate(
          frame - unfoldDelay,
          [0, 20],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );
        const unfoldV = interpolate(
          frame - unfoldDelay - 18,
          [0, 22],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );
        // 折纸三角折痕：未展开时 clip 为左上三角
        const foldRotX = interpolate(unfoldV, [0, 1], [-75, 0]);
        const foldScaleX = interpolate(unfoldH, [0, 1], [0.5, 1]);

        // 展开后的悬浮与图像缩放
        const settleOffset = Math.sin((frame - unfoldDelay - 40) / 30) * 2;

        const imgShow = interpolate(
          frame - unfoldDelay - 18,
          [6, 26],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        return (
          <div
            key={`ori-${i}`}
            style={{
              position: 'absolute',
              left: pos.x - cardSize / 2,
              top: pos.y - cardSize / 2 + dropY,
              width: cardSize,
              height: cardSize,
              opacity: dropOp,
              transform: `rotate(${wobble + (i % 2 === 0 ? -2 : 2)}deg)`,
              perspective: '1000px',
              zIndex: 10 + i,
            }}
          >
            {/* 投影 */}
            <div
              style={{
                position: 'absolute',
                left: '10%',
                right: '10%',
                bottom: -14,
                height: 22,
                background:
                  'radial-gradient(ellipse at center, rgba(60,30,10,0.45) 0%, transparent 70%)',
                filter: 'blur(4px)',
                opacity: 0.85,
              }}
            />

            {/* 折纸纸片本体 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                transformOrigin: '50% 0%',
                transform: `rotateX(${foldRotX}deg) scaleX(${foldScaleX}) translateY(${settleOffset}px)`,
                background:
                  i % 3 === 0
                    ? 'linear-gradient(135deg, #f8ecd3 0%, #e9d7ab 100%)'
                    : i % 3 === 1
                      ? 'linear-gradient(135deg, #f2dbd0 0%, #d7a896 100%)'
                      : 'linear-gradient(135deg, #e0e6cd 0%, #b6c69a 100%)',
                boxShadow:
                  '0 10px 24px rgba(80,50,20,0.35), inset 0 0 0 2px rgba(255,240,210,0.35)',
              }}
            >
              {/* 折痕（斜对角） */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(135deg, rgba(0,0,0,0.0) 49.5%, rgba(80,40,20,0.35) 50%, rgba(0,0,0,0) 50.5%), linear-gradient(45deg, rgba(0,0,0,0) 49.6%, rgba(80,40,20,0.22) 50%, rgba(0,0,0,0) 50.4%)',
                  pointerEvents: 'none',
                }}
              />

              {/* 露出的图片（居中嵌入，边缘有折起的阴影） */}
              <div
                style={{
                  position: 'absolute',
                  inset: '12%',
                  overflow: 'hidden',
                  borderRadius: 3,
                  opacity: imgShow,
                  transform: `scale(${0.86 + imgShow * 0.14})`,
                  boxShadow:
                    'inset 0 0 0 2px rgba(255,245,210,0.7), 0 4px 10px rgba(60,20,10,0.35)',
                }}
              >
                <Img
                  src={images[i % images.length]}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(0.95) contrast(1.02)',
                  }}
                />
                {/* 像素风和风渐变蒙 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, transparent 60%, rgba(90,60,40,0.3) 100%)',
                  }}
                />
              </div>

              {/* 左下角季节字 */}
              <div
                style={{
                  position: 'absolute',
                  left: 10,
                  bottom: 8,
                  fontFamily: '"STKaiti", "KaiTi", serif',
                  fontSize: 18,
                  letterSpacing: 2,
                  color: 'rgba(110,60,30,0.9)',
                  opacity: imgShow,
                  textShadow: '1px 1px 0 rgba(255,240,210,0.6)',
                }}
              >
                {labels[i % labels.length]}
              </div>

              {/* 右上角编号 */}
              <div
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 6,
                  fontFamily: '"Courier New", monospace',
                  fontSize: 9,
                  letterSpacing: 2,
                  color: 'rgba(90,45,20,0.7)',
                  opacity: imgShow,
                }}
              >
                Nº {String(i + 1).padStart(2, '0')}
              </div>
            </div>
          </div>
        );
      })}

      {/* 底部左侧条目列表 */}
      <div
        style={{
          position: 'absolute',
          left: 58,
          bottom: 36,
          fontFamily: '"STKaiti", "KaiTi", serif',
          fontSize: 12,
          letterSpacing: 6,
          color: 'rgba(60,30,15,0.7)',
          opacity: headerOp,
        }}
      >
        {labels.slice(0, cardN).join(' · ')}
      </div>

      {/* 底部右侧编号 */}
      <div
        style={{
          position: 'absolute',
          right: 58,
          bottom: 36,
          fontFamily: '"Courier New", monospace',
          fontSize: 11,
          letterSpacing: 4,
          color: 'rgba(60,30,15,0.6)',
          opacity: headerOp,
        }}
      >
        帖 · VI · MMXXVI
      </div>

      {/* 四角装饰花纹 */}
      {[
        { left: 24, top: 24 },
        { right: 24, top: 24 },
        { left: 24, bottom: 24 },
        { right: 24, bottom: 24 },
      ].map((pos, i) => (
        <div
          key={`corner-${i}`}
          style={{
            position: 'absolute',
            ...pos,
            width: 28,
            height: 28,
            border: '1px solid rgba(90,50,20,0.5)',
            borderImage:
              'linear-gradient(45deg, rgba(90,50,20,0.6), transparent) 1',
            opacity: headerOp * 0.8,
          }}
        />
      ))}

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(80,50,20,0.45) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #2  ImageNeonArcade —— 赛博朋克霓虹街机
// 特点：
// · 中央大 CRT 屏幕 + 左右两块侧屏，每屏展示一张图片
// · CRT 弯曲（border-radius 模拟） + 扫描线 + RGB 色差 + 噪点
// · 地面透视霓虹网格（紫/青），远处山峦轮廓
// · 顶部霓虹标题（闪烁），右上角"INSERT COIN"提示
// · 旋转的霓虹环 / 飘浮的像素尘
// ---------------------------------------------------------------------
export const ImageNeonArcade: React.FC<{
  images?: string[];
  title?: string;
  subtitle?: string;
  cabinetName?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  title = 'NEON ARCADE',
  subtitle = 'HYPER · RETRO · 2099',
  cabinetName = 'CRT-Ω · CABINET 07',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 屏幕布局
  const mainW = width * 0.42;
  const mainH = mainW * 0.62;
  const mainX = width / 2 - mainW / 2;
  const mainY = height * 0.44 - mainH / 2;

  const sideW = width * 0.22;
  const sideH = sideW * 0.62;

  // CRT 扫描线
  const scanY = (frame * 6) % height;

  // INSERT COIN 闪烁
  const coinBlink = Math.sin(frame / 6) > 0;

  // 入场
  const headerOp = interpolate(frame, [0, 24], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const gridOp = interpolate(frame, [8, 44], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 网格地板 消失点 参数
  const horizonY = height * 0.62;

  // 像素尘
  const pixels = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; s: number; c: number }> =
      [];
    for (let i = 0; i < 100; i++) {
      arr.push({
        x: rand(i * 1.7) * width,
        y: rand(i * 3.3) * height,
        r: 1 + Math.floor(rand(i * 5.1) * 3),
        s: i,
        c: rand(i * 2.9),
      });
    }
    return arr;
  }, [width, height]);

  // CRT 屏幕组件
  const CRT: React.FC<{
    x: number;
    y: number;
    w: number;
    h: number;
    img: string;
    accent: string;
    label: string;
    delay?: number;
  }> = ({ x, y, w, h, img, accent, label, delay = 0 }) => {
    const ent = spring({
      frame: frame - delay,
      fps,
      config: { damping: 16, stiffness: 130, mass: 0.8 },
    });
    const ySway = Math.sin((frame + delay) / 50) * 3;
    const flicker = 0.92 + Math.abs(Math.sin(frame / 3 + delay)) * 0.08;
    return (
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y + ySway,
          width: w,
          height: h + 54,
          transform: `scale(${0.6 + ent * 0.4})`,
          opacity: ent,
        }}
      >
        {/* 机柜外壳 */}
        <div
          style={{
            position: 'absolute',
            inset: -12,
            borderRadius: 18,
            background:
              'linear-gradient(180deg, #1b0e2a 0%, #0d0718 100%)',
            border: `2px solid ${accent}`,
            boxShadow: `0 0 26px ${accent}aa, inset 0 0 24px rgba(0,0,0,0.9)`,
          }}
        />
        {/* 边角螺丝 */}
        {[
          { l: 4, t: 4 },
          { r: 4, t: 4 },
          { l: 4, b: 60 },
          { r: 4, b: 60 },
        ].map((p, k) => (
          <div
            key={`screw-${k}`}
            style={{
              position: 'absolute',
              left: 'l' in p ? p.l : undefined,
              top: 't' in p ? p.t : undefined,
              right: 'r' in p ? p.r : undefined,
              bottom: 'b' in p ? p.b : undefined,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#1a1126',
              border: `1px solid ${accent}`,
              boxShadow: `0 0 6px ${accent}aa`,
            }}
          />
        ))}

        {/* 屏幕 CRT 曲面 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: w,
            height: h,
            borderRadius: '10% / 14%',
            overflow: 'hidden',
            background: '#000',
            boxShadow: `inset 0 0 24px rgba(0,0,0,0.9), inset 0 0 0 2px ${accent}55`,
            transform: `perspective(700px) rotateX(4deg)`,
            opacity: flicker,
          }}
        >
          {/* 图像（轻微 RGB 色差） */}
          <Img
            src={img}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'saturate(1.15) contrast(1.08) brightness(0.95)',
            }}
          />
          {/* RGB 色差叠加 */}
          <Img
            src={img}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              mixBlendMode: 'screen',
              opacity: 0.32,
              transform: 'translateX(2px)',
              filter: 'saturate(0) brightness(1) sepia(1) hue-rotate(-50deg)',
            }}
          />
          <Img
            src={img}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              mixBlendMode: 'screen',
              opacity: 0.32,
              transform: 'translateX(-2px)',
              filter: 'saturate(0) brightness(1) sepia(1) hue-rotate(150deg)',
            }}
          />
          {/* 扫描线 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'repeating-linear-gradient(0deg, rgba(0,0,0,0.28) 0 1px, transparent 1px 3px)',
              pointerEvents: 'none',
            }}
          />
          {/* 亮条扫描 */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: scanY % h,
              height: 28,
              background:
                'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.14) 50%, transparent 100%)',
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />
          {/* 暗角（模拟 CRT 边缘变暗） */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.72) 100%)',
              pointerEvents: 'none',
            }}
          />
          {/* 右上红灯 */}
          <div
            style={{
              position: 'absolute',
              right: 10,
              top: 10,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: coinBlink ? '#ff3f6d' : '#5a0d24',
              boxShadow: coinBlink ? '0 0 10px #ff3f6d' : undefined,
            }}
          />
          {/* 左下角 HUD */}
          <div
            style={{
              position: 'absolute',
              left: 10,
              bottom: 8,
              fontFamily: '"Courier New", monospace',
              fontSize: 9,
              letterSpacing: 2,
              color: accent,
              textShadow: `0 0 6px ${accent}`,
            }}
          >
            {label} · SIGNAL OK
          </div>
        </div>

        {/* 底部控制面板 */}
        <div
          style={{
            position: 'absolute',
            left: -10,
            right: -10,
            top: h + 6,
            height: 46,
            borderRadius: 8,
            background:
              'linear-gradient(180deg, #1a0d2a 0%, #0a0414 100%)',
            border: `1.5px solid ${accent}55`,
            boxShadow: `0 0 12px ${accent}44, inset 0 0 10px rgba(0,0,0,0.9)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            padding: '0 12px',
          }}
        >
          {/* 摇杆 */}
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 35% 30%, #ff7ab6 0%, #a01a63 100%)',
              boxShadow: `0 0 10px #ff7ab6, inset 0 -3px 5px rgba(0,0,0,0.6)`,
            }}
          />
          {/* 按钮 */}
          {['#39f3ff', '#ffd93c', '#ff6bc7'].map((c, k) => (
            <div
              key={`btn-${k}`}
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: c,
                boxShadow: `0 0 10px ${c}, inset 0 -3px 4px rgba(0,0,0,0.55)`,
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #120320 0%, #1b0734 45%, #260a52 72%, #3b0a5e 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 远山轮廓（剪影） */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="sunGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffe14b" />
            <stop offset="50%" stopColor="#ff5bc0" />
            <stop offset="100%" stopColor="#8a1fe0" />
          </linearGradient>
        </defs>
        {/* 合成日落 */}
        <circle
          cx={width / 2}
          cy={horizonY - 4}
          r={Math.min(width, height) * 0.26}
          fill="url(#sunGrad)"
          opacity={0.9}
        />
        {/* 日落横线 */}
        {[0.72, 0.78, 0.84, 0.9].map((p, i) => (
          <rect
            key={`sunline-${i}`}
            x={width / 2 - Math.min(width, height) * 0.26}
            y={horizonY - 4 + Math.min(width, height) * 0.26 * p - p * 10}
            width={Math.min(width, height) * 0.52}
            height={3}
            fill="#120320"
            opacity={0.85}
          />
        ))}
        {/* 山体 */}
        <polygon
          points={`0,${horizonY} ${width * 0.12},${horizonY - 54} ${width * 0.22},${horizonY - 20} ${width * 0.32},${horizonY - 70} ${width * 0.45},${horizonY - 10} ${width * 0.6},${horizonY - 80} ${width * 0.74},${horizonY - 24} ${width * 0.88},${horizonY - 62} ${width},${horizonY} ${width},${horizonY} 0,${horizonY}`}
          fill="#0b0216"
          opacity={0.95}
        />
      </svg>

      {/* 霓虹网格地板 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: gridOp }}
      >
        <defs>
          <linearGradient id="gridFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#39f3ff" stopOpacity="0" />
            <stop offset="30%" stopColor="#39f3ff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#ff48d0" stopOpacity="0.6" />
          </linearGradient>
        </defs>
        {/* 水平线（越往下越密） */}
        {Array.from({ length: 16 }).map((_, i) => {
          const t = i / 15;
          const eased = Math.pow(t, 2.4);
          const yOffset = ((frame * 3) / 1) % 40;
          const y = horizonY + eased * (height - horizonY) + (yOffset * t);
          return (
            <line
              key={`gh-${i}`}
              x1={0}
              y1={y}
              x2={width}
              y2={y}
              stroke="url(#gridFade)"
              strokeWidth={0.8 + t * 1.6}
              opacity={0.75}
            />
          );
        })}
        {/* 垂直线（消失点透视） */}
        {Array.from({ length: 21 }).map((_, i) => {
          const t = (i - 10) / 10;
          const xTop = width / 2 + t * width * 0.06;
          const xBot = width / 2 + t * width * 1.6;
          return (
            <line
              key={`gv-${i}`}
              x1={xTop}
              y1={horizonY}
              x2={xBot}
              y2={height}
              stroke="url(#gridFade)"
              strokeWidth={0.8}
              opacity={0.6}
            />
          );
        })}
      </svg>

      {/* 像素粒子 */}
      {pixels.map((p) => {
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame / 6 + p.s));
        const drift = Math.sin(frame / 20 + p.s) * 4;
        return (
          <div
            key={`px-${p.s}`}
            style={{
              position: 'absolute',
              left: p.x + drift,
              top: (p.y + frame * 0.3) % height,
              width: p.r,
              height: p.r,
              background: p.c < 0.33 ? '#39f3ff' : p.c < 0.66 ? '#ff48d0' : '#ffd93c',
              opacity: tw * 0.75,
              boxShadow: `0 0 6px currentColor`,
            }}
          />
        );
      })}

      {/* 左侧屏 */}
      <CRT
        x={width * 0.08}
        y={height * 0.44 - sideH / 2 + 20}
        w={sideW}
        h={sideH}
        img={images[1 % images.length]}
        accent="#39f3ff"
        label="CH.01"
        delay={18}
      />
      {/* 主屏 */}
      <CRT
        x={mainX}
        y={mainY}
        w={mainW}
        h={mainH}
        img={images[0]}
        accent="#ff48d0"
        label="CH.MAIN"
        delay={2}
      />
      {/* 右侧屏 */}
      <CRT
        x={width * 0.92 - sideW}
        y={height * 0.44 - sideH / 2 + 20}
        w={sideW}
        h={sideH}
        img={images[2 % images.length]}
        accent="#ffd93c"
        label="CH.02"
        delay={28}
      />

      {/* 顶部霓虹标题 */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerOp,
          zIndex: 200,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 12,
            letterSpacing: 10,
            color: '#39f3ff',
            textShadow: '0 0 8px #39f3ff',
          }}
        >
          ▌ {cabinetName} ▐
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: '"Impact", "Helvetica Neue", sans-serif',
            fontSize: 48,
            letterSpacing: 10,
            color: '#ffffff',
            textShadow:
              '0 0 10px #ff48d0, 0 0 22px #ff48d0, 2px 2px 0 #39f3ff, -2px -2px 0 #ffd93c',
            fontWeight: 900,
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Courier New", monospace',
            fontSize: 13,
            letterSpacing: 9,
            color: '#ff9ae3',
            textShadow: '0 0 6px #ff48d0',
          }}
        >
          ◆ {subtitle} ◆
        </div>
      </div>

      {/* 右上角 INSERT COIN */}
      <div
        style={{
          position: 'absolute',
          right: 38,
          top: 130,
          fontFamily: '"Courier New", monospace',
          fontSize: 13,
          letterSpacing: 4,
          color: coinBlink ? '#ffd93c' : 'rgba(255,217,60,0.2)',
          textShadow: coinBlink ? '0 0 10px #ffd93c' : undefined,
          padding: '6px 12px',
          border: `1.5px solid ${coinBlink ? '#ffd93c' : 'rgba(255,217,60,0.3)'}`,
          background: 'rgba(10,4,20,0.55)',
          opacity: headerOp,
        }}
      >
        ▶ INSERT COIN
      </div>

      {/* 左下角 HI-SCORE */}
      <div
        style={{
          position: 'absolute',
          left: 40,
          bottom: 28,
          fontFamily: '"Courier New", monospace',
          fontSize: 12,
          letterSpacing: 3,
          color: '#39f3ff',
          textShadow: '0 0 6px #39f3ff',
          opacity: headerOp,
        }}
      >
        HI-SCORE · {String(9 + (frame % 99999)).padStart(7, '0')}
      </div>
      {/* 右下角 PLAYER */}
      <div
        style={{
          position: 'absolute',
          right: 40,
          bottom: 28,
          fontFamily: '"Courier New", monospace',
          fontSize: 12,
          letterSpacing: 3,
          color: '#ff48d0',
          textShadow: '0 0 6px #ff48d0',
          opacity: headerOp,
        }}
      >
        1P · READY
      </div>

      {/* 整体 CRT 噪点叠加（全屏） */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.1) 0 1px, transparent 1px 3px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          opacity: 0.55,
        }}
      />
      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.75) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #3  ImageCinemaScopeTrailer —— 2.39:1 宽银幕电影预告
// 特点：
// · 每张图片以 Ken Burns 缓慢推拉 + 偏移，全屏填充
// · 上下黑色 letterbox 条（入场滑入）+ 胶片级时码 / 机型 HUD
// · 图与图之间有白色胶片烧穿闪光、交叉溶解
// · 持续胶片颗粒、少量灰尘、偶发划痕
// · 结尾呈现大标题 / tagline / "COMING · MMXXVI" 字幕
// ---------------------------------------------------------------------
export const ImageCinemaScopeTrailer: React.FC<{
  images?: string[];
  shotTitles?: string[];
  filmTitle?: string;
  tagline?: string;
  studio?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  shotTitles = [
    'I. First Light',
    'II. The Crossing',
    'III. Silver Hour',
    'IV. Hollow Song',
    'V. Fade to Dawn',
  ],
  filmTitle = 'AURORA',
  tagline = 'A FILM IN FIVE FRAMES',
  studio = 'WREN · CINEMATIQUE',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const shotCount = Math.min(images.length, 5);
  const shotLen = 48;
  const transitionLen = 10;
  const outroStart = shotCount * shotLen;

  const isOutro = frame >= outroStart;
  const currentShot = Math.min(
    Math.max(0, Math.floor(frame / shotLen)),
    shotCount - 1,
  );
  const localFrame = frame - currentShot * shotLen;

  // Letterbox 入场
  const barProgress = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140, mass: 0.7 },
  });
  const barH = height * 0.13;

  // Ken Burns
  const dir = currentShot % 2 === 0 ? 1 : -1;
  const prog = Math.min(1, Math.max(0, localFrame / shotLen));
  const kenScale = 1.07 + prog * 0.09;
  const kenX = interpolate(prog, [0, 1], [-22 * dir, 22 * dir]);
  const kenY = interpolate(prog, [0, 1], [-8 * dir, 8 * dir]);

  // 过渡：本镜头渐暗、下张渐出，中间白闪
  const fadeOut =
    localFrame > shotLen - transitionLen
      ? (localFrame - (shotLen - transitionLen)) / transitionLen
      : 0;
  const nextIndex = currentShot + 1;
  const hasNext = nextIndex < shotCount;

  // 时间码
  const totalSeconds = frame / fps;
  const tc = `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(
    Math.floor(totalSeconds % 60),
  ).padStart(2, '0')}:${String(frame % fps).padStart(2, '0')}`;

  // 胶片灰尘 / 划痕（伪随机）
  const dust = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; size: number; life: number }> = [];
    for (let i = 0; i < 30; i++) {
      arr.push({
        x: rand(i * 13.7),
        y: rand(i * 7.1),
        size: 1 + rand(i * 3.3) * 3,
        life: rand(i * 5.5) * 30 + 10,
      });
    }
    return arr;
  }, []);
  const scratches = React.useMemo(() => {
    const arr: Array<{ x: number; life: number }> = [];
    for (let i = 0; i < 6; i++) {
      arr.push({ x: rand(i * 2.7), life: rand(i * 9.1) * 40 + 20 });
    }
    return arr;
  }, []);

  // 片尾入场
  const outroProg = interpolate(
    frame - outroStart,
    [0, 40],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );
  const outroLine = interpolate(
    frame - outroStart,
    [20, 60],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill style={{ background: '#000', overflow: 'hidden' }}>
      <ScaledStage>
      {/* 当前镜头 */}
      {!isOutro && (
        <div style={{ position: 'absolute', inset: 0, opacity: 1 - fadeOut * 0.5 }}>
          <Img
            src={images[currentShot]}
            style={{
              position: 'absolute',
              left: '-8%',
              top: '-8%',
              width: '116%',
              height: '116%',
              objectFit: 'cover',
              transform: `scale(${kenScale}) translate(${kenX}px, ${kenY}px)`,
              filter: 'contrast(1.06) saturate(1.05) brightness(0.93)',
            }}
          />
          {/* 青橙分级 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(14,36,56,0.4) 0%, rgba(0,0,0,0) 40%, rgba(90,35,10,0.4) 100%)',
              mixBlendMode: 'multiply',
            }}
          />
        </div>
      )}

      {/* 下一镜头（过渡期出现） */}
      {!isOutro && hasNext && fadeOut > 0 && (
        <div style={{ position: 'absolute', inset: 0, opacity: fadeOut }}>
          <Img
            src={images[nextIndex]}
            style={{
              position: 'absolute',
              left: '-8%',
              top: '-8%',
              width: '116%',
              height: '116%',
              objectFit: 'cover',
              transform: `scale(1.08)`,
              filter: 'contrast(1.06) saturate(1.05) brightness(0.93)',
            }}
          />
          {/* 白色胶片烧穿闪光 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,240,210,0.75)',
              opacity: Math.sin(fadeOut * Math.PI),
              mixBlendMode: 'screen',
            }}
          />
        </div>
      )}

      {/* 镜头内 暗角 */}
      {!isOutro && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.78) 100%)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* 片尾卡片 */}
      {isOutro && (
        <AbsoluteFill
          style={{
            background:
              'radial-gradient(ellipse at 50% 55%, #1a0d08 0%, #000 70%)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontFamily:
                  '"Didot","Playfair Display","Georgia",serif',
                fontSize: 104,
                fontWeight: 700,
                color: '#f5e6c8',
                letterSpacing: 20,
                textShadow: '0 0 32px rgba(245,230,200,0.3)',
                opacity: outroProg,
                transform: `scale(${0.92 + outroProg * 0.08})`,
              }}
            >
              {filmTitle}
            </div>
            <div
              style={{
                marginTop: 28,
                fontFamily: '"Courier New", monospace',
                fontSize: 14,
                letterSpacing: 9,
                color: '#c7a97a',
                opacity: outroProg * 0.9,
              }}
            >
              {tagline}
            </div>
            <div
              style={{
                margin: '38px auto 0',
                width: outroLine * 260,
                height: 1,
                background: '#8f7144',
              }}
            />
            <div
              style={{
                marginTop: 20,
                fontFamily: '"Courier New", monospace',
                fontSize: 11,
                letterSpacing: 7,
                color: '#8f7144',
                opacity: outroLine,
              }}
            >
              COMING · MMXXVI
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* 尘埃 */}
      {!isOutro &&
        dust.map((d, i) => {
          const on = Math.floor(frame / d.life) % 4 === i % 4;
          if (!on) return null;
          return (
            <div
              key={`cs-dust-${i}`}
              style={{
                position: 'absolute',
                left: d.x * width,
                top: d.y * height,
                width: d.size,
                height: d.size,
                background: 'rgba(255,240,220,0.55)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />
          );
        })}

      {/* 划痕 */}
      {!isOutro &&
        scratches.map((s, i) => {
          const t = (frame % s.life) / s.life;
          if (t < 0.02 || t > 0.12 + i * 0.03) return null;
          return (
            <div
              key={`cs-scr-${i}`}
              style={{
                position: 'absolute',
                left: s.x * width,
                top: 0,
                bottom: 0,
                width: 1,
                background: 'rgba(255,240,220,0.28)',
                pointerEvents: 'none',
              }}
            />
          );
        })}

      {/* Letterbox 上条 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: barH,
          background: '#000',
          transform: `translateY(${-barH + barProgress * barH}px)`,
          borderBottom: '1px solid #1a1a1a',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 40px',
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            color: '#b58845',
            letterSpacing: 4,
          }}
        >
          ● REC · TC {tc} · REEL 01
        </div>
        <div
          style={{
            fontFamily: '"Didot","Playfair Display","Georgia",serif',
            fontSize: 16,
            color: '#e7c889',
            letterSpacing: 10,
          }}
        >
          {studio}
        </div>
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            color: '#b58845',
            letterSpacing: 4,
          }}
        >
          2.39:1 · ANAMORPHIC · DOLBY
        </div>
      </div>

      {/* Letterbox 下条 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: barH,
          background: '#000',
          transform: `translateY(${barH - barProgress * barH}px)`,
          borderTop: '1px solid #1a1a1a',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 40px',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: '"Didot","Playfair Display","Georgia",serif',
              fontSize: 28,
              color: '#f5e6c8',
              letterSpacing: 6,
            }}
          >
            {isOutro
              ? filmTitle
              : shotTitles[currentShot % shotTitles.length]}
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: '"Courier New", monospace',
              fontSize: 10,
              color: '#8f7144',
              letterSpacing: 3,
            }}
          >
            SHOT {String(currentShot + 1).padStart(2, '0')} /{' '}
            {String(shotCount).padStart(2, '0')}
          </div>
        </div>

        {/* 镜头进度 */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {Array.from({ length: shotCount }).map((_, i) => (
            <div
              key={`cs-pb-${i}`}
              style={{
                width: i === currentShot ? 48 : 14,
                height: 3,
                background:
                  i <= currentShot
                    ? '#e7c889'
                    : 'rgba(231,200,137,0.25)',
                boxShadow:
                  i === currentShot
                    ? '0 0 8px rgba(231,200,137,0.6)'
                    : undefined,
              }}
            />
          ))}
        </div>

        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            color: '#b58845',
            letterSpacing: 4,
            textAlign: 'right',
            lineHeight: 1.5,
          }}
        >
          ARRI ALEXA · KODAK 5219
          <br />
          <span style={{ color: '#8f7144' }}>
            f/2.8 · 1/48 · ISO 800
          </span>
        </div>
      </div>

      {/* 全局颗粒 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(255,240,220,0.025) 0 1px, transparent 1px 2px)',
          mixBlendMode: 'overlay',
          opacity: 0.45,
          pointerEvents: 'none',
          zIndex: 300,
        }}
      />

      {/* 投影灯闪烁 */}
      <AbsoluteFill
        style={{
          background: '#000',
          opacity: (Math.sin(frame / 2) * 0.5 + 0.5) * 0.04,
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #4  ImageCrystalPrism —— 悬浮旋转六面水晶立方
// 特点：
// · 星云 + 星空深色宇宙背景，彩色光束绕立方旋转
// · 中央一只大水晶立方，六个面各嵌一张图
// · 立方持续 X/Y 旋转 + 轻微 Z 摆动，悬浮呼吸
// · 每一面有彩虹油膜（hue 随时间迁移）、亮带扫过高光
// · 立方底部投射椭圆光晕（地面辉光）
// · 标题大字 + 旋转角度 HUD
// ---------------------------------------------------------------------
export const ImageCrystalPrism: React.FC<{
  images?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  title = 'CRYSTAL',
  subtitle = 'Six Windows of Light · Facets of the Hour',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const cubeSize = Math.min(width, height) * 0.44;

  // 旋转
  const rotY = frame * 0.7;
  const rotX = Math.sin(frame / 150) * 18 + 12;
  const rotZ = Math.sin(frame / 90) * 3;
  const floatY = Math.sin(frame / 52) * 10;

  // 入场
  const ent = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 110, mass: 0.8 },
  });
  const cubeScale = 0.6 + ent * 0.4;

  // 星空
  const stars = React.useMemo(() => {
    const arr: Array<{
      x: number;
      y: number;
      r: number;
      tw: number;
      c: number;
    }> = [];
    for (let i = 0; i < 200; i++) {
      arr.push({
        x: rand(i * 1.3),
        y: rand(i * 2.7),
        r: 0.5 + rand(i * 4.1) * 1.8,
        tw: rand(i * 5.5) * 6.28,
        c: rand(i * 7.3),
      });
    }
    return arr;
  }, []);

  const faces = [
    { transform: `translateZ(${cubeSize / 2}px)`, label: 'I' },
    { transform: `rotateY(90deg) translateZ(${cubeSize / 2}px)`, label: 'II' },
    { transform: `rotateY(180deg) translateZ(${cubeSize / 2}px)`, label: 'III' },
    { transform: `rotateY(-90deg) translateZ(${cubeSize / 2}px)`, label: 'IV' },
    { transform: `rotateX(90deg) translateZ(${cubeSize / 2}px)`, label: 'V' },
    { transform: `rotateX(-90deg) translateZ(${cubeSize / 2}px)`, label: 'VI' },
  ];

  // 光束
  const beams = Array.from({ length: 10 }).map((_, i) => i);

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 40%, #1c1448 0%, #0a0a28 52%, #04041a 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 星云 */}
      <div
        style={{
          position: 'absolute',
          left: '-10%',
          top: '-10%',
          right: '-10%',
          bottom: '-10%',
          background:
            'radial-gradient(ellipse at 30% 25%, rgba(140, 80, 220, 0.35) 0%, transparent 40%), radial-gradient(ellipse at 80% 78%, rgba(255, 90, 160, 0.28) 0%, transparent 45%), radial-gradient(ellipse at 70% 30%, rgba(50, 180, 255, 0.25) 0%, transparent 42%)',
          filter: 'blur(24px)',
        }}
      />

      {/* 星 */}
      {stars.map((s, i) => {
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame / 28 + s.tw));
        return (
          <div
            key={`cp-star-${i}`}
            style={{
              position: 'absolute',
              left: s.x * width,
              top: s.y * height,
              width: s.r,
              height: s.r,
              background:
                s.c < 0.33
                  ? '#ffd6a8'
                  : s.c < 0.66
                    ? '#aeccff'
                    : '#ffffff',
              borderRadius: '50%',
              opacity: tw,
              boxShadow: `0 0 ${s.r * 2.5}px currentColor`,
            }}
          />
        );
      })}

      {/* 光束 + 中心辉光 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          <radialGradient id="cp-glow">
            <stop offset="0%" stopColor="rgba(255,255,255,0.65)" />
            <stop offset="45%" stopColor="rgba(180,130,255,0.3)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <circle
          cx={width / 2}
          cy={height / 2 + floatY}
          r={cubeSize * 0.95}
          fill="url(#cp-glow)"
          opacity={0.5 + Math.sin(frame / 30) * 0.12}
        />
        {beams.map((i) => {
          const angle = (i / 10) * 360 + frame * 0.5;
          const rad = (angle * Math.PI) / 180;
          const len = cubeSize * 1.7;
          const hue = (i * 36 + frame * 0.8) % 360;
          return (
            <line
              key={`cp-bm-${i}`}
              x1={width / 2}
              y1={height / 2 + floatY}
              x2={width / 2 + Math.cos(rad) * len}
              y2={height / 2 + floatY + Math.sin(rad) * len}
              stroke={`hsla(${hue}, 85%, 72%, 0.16)`}
              strokeWidth={2}
            />
          );
        })}
      </svg>

      {/* 立方 */}
      <div
        style={{
          position: 'absolute',
          left: width / 2 - cubeSize / 2,
          top: height / 2 - cubeSize / 2 + floatY,
          width: cubeSize,
          height: cubeSize,
          perspective: 1800,
          opacity: ent,
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
            transform: `scale(${cubeScale}) rotateX(${rotX}deg) rotateY(${rotY}deg) rotateZ(${rotZ}deg)`,
          }}
        >
          {faces.map((f, i) => (
            <div
              key={`cp-face-${i}`}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                transform: f.transform,
                backfaceVisibility: 'hidden',
                overflow: 'hidden',
                boxShadow:
                  '0 0 44px rgba(180,130,255,0.55), inset 0 0 0 2px rgba(255,255,255,0.35)',
                background: '#000',
              }}
            >
              <Img
                src={images[i % images.length]}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'saturate(1.12) contrast(1.06) brightness(0.95)',
                }}
              />

              {/* 彩色油膜（hue 随时间与面偏移） */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(${(i * 45 + frame * 1.6) % 360}deg, rgba(255,180,220,0.16) 0%, rgba(130,220,255,0.12) 45%, rgba(255,220,130,0.14) 100%)`,
                  mixBlendMode: 'screen',
                  pointerEvents: 'none',
                }}
              />

              {/* 亮带扫过 */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${((frame * 2 + i * 55) % 260) - 60}%`,
                  width: '35%',
                  background:
                    'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.32) 50%, transparent 100%)',
                  mixBlendMode: 'screen',
                  pointerEvents: 'none',
                }}
              />

              {/* 内边光晕 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  boxShadow: 'inset 0 0 60px rgba(180,130,255,0.32)',
                  pointerEvents: 'none',
                }}
              />

              {/* 面编号 */}
              <div
                style={{
                  position: 'absolute',
                  left: 14,
                  bottom: 10,
                  fontFamily: '"Didot","Georgia",serif',
                  fontSize: 22,
                  letterSpacing: 4,
                  color: '#fff',
                  textShadow: '0 0 10px rgba(255,255,255,0.7)',
                  opacity: 0.9,
                }}
              >
                · {f.label} ·
              </div>

              {/* 右上小 HUD */}
              <div
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 10,
                  fontFamily: '"Courier New", monospace',
                  fontSize: 9,
                  letterSpacing: 2,
                  color: 'rgba(255,255,255,0.8)',
                }}
              >
                FACET/{String(i + 1).padStart(2, '0')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 地面辉光（立方下椭圆光斑） */}
      <div
        style={{
          position: 'absolute',
          left: width / 2 - cubeSize * 0.8,
          top: height / 2 + cubeSize * 0.58 + floatY,
          width: cubeSize * 1.6,
          height: 46,
          background:
            'radial-gradient(ellipse at center, rgba(180,130,255,0.55) 0%, transparent 70%)',
          filter: 'blur(8px)',
          opacity: 0.8,
        }}
      />

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 54,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: ent,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 12,
            letterSpacing: 10,
            color: '#a090ff',
            textShadow: '0 0 8px rgba(160,144,255,0.7)',
          }}
        >
          ◆ FACET · VI · REFRACTION ◆
        </div>
        <div
          style={{
            marginTop: 14,
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 70,
            letterSpacing: 22,
            color: '#fff',
            fontWeight: 300,
            textShadow:
              '0 0 20px rgba(180,130,255,0.85), 0 0 42px rgba(255,160,220,0.45)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: '"Georgia",serif',
            fontStyle: 'italic',
            fontSize: 15,
            letterSpacing: 4,
            color: '#e0c4ff',
            opacity: 0.9,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 底部 HUD */}
      <div
        style={{
          position: 'absolute',
          bottom: 34,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 52px',
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#a090ff',
          opacity: ent * 0.85,
        }}
      >
        <div>
          ROT · Y {String(Math.floor(rotY % 360)).padStart(3, '0')}° · X{' '}
          {String(Math.floor(Math.abs(rotX))).padStart(3, '0')}°
        </div>
        <div>
          PRISM · VI-FACE CRYSTAL · REFRACTIVE INDEX 2.417
        </div>
        <div>MMXXVI · FACETS OF LIGHT</div>
      </div>

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.72) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #5  ImageHolographicMuseum —— 未来全息博物馆碾压展陈
// 特点：
// · 5 块巨大全息展屏按深度呈阶梯排列，相对相机从右向左循环平移
// · 中心屏为 "主展"，占据画面最大空间且有名牌 / 目录编号
// · 侧屏逐级缩小 + 向内倾斜（3D rotateY）
// · 地面透视网格 + 顶部射光锥 + 空中悬浮灰尘 + 扫描线故障
// · 图像带青色全息色调、滚动扫描条、偶发 Glitch 色带
// · 顶部馆名大字 / 底部策展 HUD
// ---------------------------------------------------------------------
export const ImageHolographicMuseum: React.FC<{
  images?: string[];
  exhibitTitles?: string[];
  museumName?: string;
  subtitle?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  exhibitTitles = [
    'DRIFT',
    'ECHO',
    'HALCYON',
    'OBLIVION',
    'ZENITH',
    'LUMEN',
    'WRAITH',
    'AUGUR',
  ],
  museumName = 'MUSEUM OF LIGHT',
  subtitle = 'HALL NOVA · EXHIBIT 07',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const N = Math.min(images.length, 6);
  const cycleLen = 70;

  // 浮点型进度（越大越往后走）
  const t = frame / cycleLen;

  const ent = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 110, mass: 0.8 },
  });

  // 空中灰尘
  const dust = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; s: number }> = [];
    for (let i = 0; i < 120; i++) {
      arr.push({
        x: rand(i * 1.7),
        y: rand(i * 3.3),
        r: 0.5 + rand(i * 5.1) * 1.8,
        s: i,
      });
    }
    return arr;
  }, []);

  const panelW = Math.min(width, height) * 0.38;
  const panelH = panelW * 1.12;
  const centerY = height * 0.46;

  const renderPanel = (imgIdx: number, slot: number) => {
    const absSlot = Math.abs(slot);
    if (absSlot > 2.4) return null;
    const scale = Math.max(0, 1 - absSlot * 0.24);
    if (scale <= 0.02) return null;
    const x = width / 2 + slot * panelW * 0.78;
    const zDepth = absSlot * 140;
    const rotY = slot * -15;
    const opacity = Math.max(0, 1 - absSlot * 0.36);
    const isMain = absSlot < 0.3;

    const holoFlicker = 0.94 + Math.sin(frame / 3 + imgIdx) * 0.06;
    const glitchOn = isMain && Math.sin(frame / 9 + imgIdx) > 0.82;

    return (
      <div
        key={`hm-panel-${imgIdx}-${Math.round(slot * 10)}`}
        style={{
          position: 'absolute',
          left: x - (panelW * scale) / 2,
          top: centerY - (panelH * scale) / 2,
          width: panelW * scale,
          height: panelH * scale,
          transform: `translateZ(-${zDepth}px) rotateY(${rotY}deg)`,
          transformStyle: 'preserve-3d',
          opacity: opacity * ent * holoFlicker,
          zIndex: 100 - Math.floor(absSlot * 10),
        }}
      >
        {/* 发光外框 */}
        <div
          style={{
            position: 'absolute',
            inset: -6,
            border: '2px solid rgba(120,220,255,0.75)',
            boxShadow:
              '0 0 32px rgba(120,220,255,0.55), inset 0 0 14px rgba(120,220,255,0.3)',
            background: 'rgba(10,30,50,0.25)',
            pointerEvents: 'none',
          }}
        />

        {/* 四角 L 型 */}
        {(
          [
            { pos: { left: -12, top: -12 }, edges: 'LT' },
            { pos: { right: -12, top: -12 }, edges: 'RT' },
            { pos: { left: -12, bottom: -12 }, edges: 'LB' },
            { pos: { right: -12, bottom: -12 }, edges: 'RB' },
          ] as Array<{
            pos: React.CSSProperties;
            edges: 'LT' | 'RT' | 'LB' | 'RB';
          }>
        ).map((c, k) => (
          <div
            key={`hm-c-${k}`}
            style={{
              position: 'absolute',
              ...c.pos,
              width: 18,
              height: 18,
              borderLeft: c.edges.includes('L')
                ? '2px solid #8fd8ff'
                : undefined,
              borderRight: c.edges.includes('R')
                ? '2px solid #8fd8ff'
                : undefined,
              borderTop: c.edges.includes('T')
                ? '2px solid #8fd8ff'
                : undefined,
              borderBottom: c.edges.includes('B')
                ? '2px solid #8fd8ff'
                : undefined,
              boxShadow: '0 0 8px #8fd8ff',
            }}
          />
        ))}

        {/* 图像 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            background: '#000',
          }}
        >
          <Img
            src={images[imgIdx]}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter:
                'brightness(0.92) contrast(1.1) saturate(1.2) hue-rotate(-6deg)',
            }}
          />

          {/* 青色全息色调 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(60,180,255,0.22) 0%, transparent 45%, rgba(120,220,255,0.22) 100%)',
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />

          {/* 扫描线 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'repeating-linear-gradient(0deg, rgba(120,220,255,0.1) 0 1px, transparent 1px 3px)',
              pointerEvents: 'none',
            }}
          />

          {/* 移动亮条 */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: ((frame * 1.6 + imgIdx * 50) % (panelH * scale + 80)) - 40,
              height: 44,
              background:
                'linear-gradient(180deg, transparent 0%, rgba(120,220,255,0.28) 50%, transparent 100%)',
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />

          {/* 故障色带（只出现在主屏） */}
          {glitchOn && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${(frame * 5) % 100}%`,
                height: 4,
                background: 'rgba(255,100,150,0.4)',
                mixBlendMode: 'screen',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        {/* 中心屏名牌 */}
        {isMain && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: panelH * scale + 14,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                fontFamily: '"Didot","Georgia",serif',
                fontSize: 26,
                letterSpacing: 10,
                color: '#e0f4ff',
                textShadow: '0 0 14px rgba(120,220,255,0.85)',
              }}
            >
              {exhibitTitles[imgIdx % exhibitTitles.length]}
            </div>
            <div
              style={{
                marginTop: 5,
                fontFamily: '"Courier New",monospace',
                fontSize: 10,
                letterSpacing: 5,
                color: '#8fd8ff',
              }}
            >
              CAT. N° {String(imgIdx + 1).padStart(3, '0')} · MIXED MEDIA · 2026
            </div>
          </div>
        )}

        {/* 底座辉光 */}
        <div
          style={{
            position: 'absolute',
            left: '8%',
            right: '8%',
            bottom: -22,
            height: 8,
            background:
              'radial-gradient(ellipse at center, rgba(120,220,255,0.95), transparent 70%)',
            filter: 'blur(5px)',
            pointerEvents: 'none',
          }}
        />
      </div>
    );
  };

  // 渲染 5 张展屏（k = -2..2，以 t 的小数部分为偏移）
  const panels: Array<React.ReactNode> = [];
  const baseIdx = Math.floor(t);
  const frac = t - baseIdx;
  for (let k = -2; k <= 2; k++) {
    const idx = ((baseIdx + k) % N + N) % N;
    const slot = k - frac;
    panels.push(renderPanel(idx, slot));
  }

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #020612 0%, #071428 45%, #0a1f3a 75%, #112a4a 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 飘浮灰尘 */}
      {dust.map((d, i) => {
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame / 32 + d.s));
        return (
          <div
            key={`hm-du-${i}`}
            style={{
              position: 'absolute',
              left: d.x * width,
              top: (d.y * height + frame * 0.25) % height,
              width: d.r,
              height: d.r,
              background: '#8fd8ff',
              borderRadius: '50%',
              opacity: tw * 0.65,
              boxShadow: '0 0 4px #8fd8ff',
            }}
          />
        );
      })}

      {/* 顶光锥 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="hm-spot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(120,220,255,0.45)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        {[0.14, 0.32, 0.5, 0.68, 0.86].map((xp, i) => (
          <polygon
            key={`hm-sp-${i}`}
            points={`${width * xp - 18},0 ${width * xp + 18},0 ${width * xp + 110},${height * 0.78} ${width * xp - 110},${height * 0.78}`}
            fill="url(#hm-spot)"
            opacity={0.28 + Math.sin(frame / 44 + i) * 0.1}
          />
        ))}
      </svg>

      {/* 地面透视网格 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="hm-grid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8fd8ff" stopOpacity="0" />
            <stop offset="20%" stopColor="#8fd8ff" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#8fd8ff" stopOpacity="1" />
          </linearGradient>
        </defs>
        {Array.from({ length: 16 }).map((_, i) => {
          const ty = i / 15;
          const ey = Math.pow(ty, 2.2);
          const yOff = (frame * 2.6) % 36;
          const y = height * 0.62 + ey * height * 0.38 + yOff * ty;
          return (
            <line
              key={`hm-gh-${i}`}
              x1={0}
              y1={y}
              x2={width}
              y2={y}
              stroke="url(#hm-grid)"
              strokeWidth={0.6 + ty * 1.3}
            />
          );
        })}
        {Array.from({ length: 21 }).map((_, i) => {
          const t2 = (i - 10) / 10;
          return (
            <line
              key={`hm-gv-${i}`}
              x1={width / 2 + t2 * width * 0.06}
              y1={height * 0.62}
              x2={width / 2 + t2 * width * 1.9}
              y2={height}
              stroke="rgba(120,220,255,0.28)"
              strokeWidth={0.7}
            />
          );
        })}
      </svg>

      {/* 展屏阵列（开启透视） */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          perspective: 1700,
        }}
      >
        {panels}
      </div>

      {/* 顶部馆名 */}
      <div
        style={{
          position: 'absolute',
          top: 38,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: ent,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 10,
            color: '#8fd8ff',
            textShadow: '0 0 8px #8fd8ff',
          }}
        >
          ◇ {subtitle} ◇
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 48,
            letterSpacing: 18,
            color: '#e0f4ff',
            textShadow:
              '0 0 14px rgba(120,220,255,0.75), 0 0 32px rgba(120,220,255,0.4)',
            fontWeight: 300,
          }}
        >
          {museumName}
        </div>
        <div
          style={{
            margin: '12px auto 0',
            width: 200,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, #8fd8ff, transparent)',
          }}
        />
      </div>

      {/* 底部 HUD */}
      <div
        style={{
          position: 'absolute',
          bottom: 28,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 46px',
          fontFamily: '"Courier New",monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#8fd8ff',
          opacity: ent * 0.9,
        }}
      >
        <div>▸ CURATOR · NOVA INSTITUTE</div>
        <div>
          EXHIBIT {String((baseIdx % N) + 1).padStart(2, '0')} /{' '}
          {String(N).padStart(2, '0')} · HOLO-RENDERED
        </div>
        <div>◀ ARCHIVE · MMXXVI ▶</div>
      </div>

      {/* 两侧暗墙 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '14%',
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.65), transparent)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '14%',
          background:
            'linear-gradient(-90deg, rgba(0,0,0,0.65), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* 全局扫描线 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.1) 0 1px, transparent 1px 4px)',
          pointerEvents: 'none',
          mixBlendMode: 'overlay',
          opacity: 0.55,
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,0.78) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #6  ImageLiquidChromeGallery —— 液态铬画廊
// 特点：
// · 高级质感深灰背景 + 悬浮金属粒子
// · 中央一块巨幅液态铬金属画板（85% 宽），完整展示图片
// · 画板四周是流动的液态金属边框（镜面高光持续移动）
// · 画板表面漂浮彩虹油膜（hue 随时间漂移 + 镜面光带扫过）
// · 切换图片时从中心爆开"金属涟漪"环（多层同心圆向外扩散）
// · 顶部 + 底部极简金属字排版，侧面索引条
// ---------------------------------------------------------------------
export const ImageLiquidChromeGallery: React.FC<{
  images?: string[];
  labels?: string[];
  title?: string;
  subtitle?: string;
  secondsPerImage?: number;
}> = ({
  images = DEFAULT_IMAGES_8,
  labels,
  title = 'LIQUID · CHROME',
  subtitle = 'MERCURY SERIES · MMXXVI',
  secondsPerImage = 2.2,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const N = images.length;
  const perShot = Math.max(24, Math.round(fps * secondsPerImage));
  const idx = Math.floor(frame / perShot) % N;
  const prevIdx = (idx - 1 + N) % N;
  const localFrame = frame - Math.floor(frame / perShot) * perShot;

  // 画板尺寸
  const panelW = width * 0.78;
  const panelH = height * 0.68;
  const panelX = (width - panelW) / 2;
  const panelY = (height - panelH) / 2 + 10;

  // 入场
  const ent = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 110, mass: 0.9 },
  });

  // 涟漪进度（每次切换从 0→1）
  const ripple = interpolate(localFrame, [0, 36], [0, 1], {
    extrapolateRight: 'clamp',
  });
  // 过渡淡入
  const fadeIn = interpolate(localFrame, [0, 14], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 漂浮金属粒子
  const particles = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; s: number; speed: number }> = [];
    for (let i = 0; i < 80; i++) {
      arr.push({
        x: rand(i * 1.7) * width,
        y: rand(i * 3.3) * height,
        r: 0.6 + rand(i * 5.1) * 2.2,
        s: i,
        speed: 0.15 + rand(i * 2.9) * 0.6,
      });
    }
    return arr;
  }, [width, height]);

  // 镜面光带水平位置（周期循环）
  const sweep = ((frame * 3) % (panelW + 240)) - 120;
  // hue 漂移（彩虹油膜）
  const hue = (frame * 0.6) % 360;

  // 涟漪环数（最多 4 层）
  const rippleRings = [0, 0.15, 0.3, 0.45];

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 40%, #2a2d33 0%, #15171c 52%, #070809 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景星辉 / 金属雾 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 25% 20%, rgba(180,200,255,0.18) 0%, transparent 40%), radial-gradient(ellipse at 82% 78%, rgba(255,180,210,0.15) 0%, transparent 42%)',
          filter: 'blur(28px)',
        }}
      />

      {/* 金属粒子 */}
      {particles.map((p) => {
        const y = (p.y + frame * p.speed) % height;
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame / 10 + p.s));
        return (
          <div
            key={`lc-pt-${p.s}`}
            style={{
              position: 'absolute',
              left: p.x,
              top: y,
              width: p.r,
              height: p.r,
              background: '#e8ecf5',
              opacity: tw * 0.55,
              borderRadius: '50%',
              boxShadow: '0 0 6px rgba(220,230,255,0.7)',
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* 地面反光椭圆 */}
      <div
        style={{
          position: 'absolute',
          left: width * 0.18,
          right: width * 0.18,
          bottom: -60,
          height: 160,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(200,210,235,0.32) 0%, transparent 70%)',
          filter: 'blur(16px)',
          pointerEvents: 'none',
        }}
      />

      {/* 主画板（液态铬） */}
      <div
        style={{
          position: 'absolute',
          left: panelX,
          top: panelY,
          width: panelW,
          height: panelH,
          transform: `scale(${0.82 + ent * 0.18})`,
          opacity: ent,
        }}
      >
        {/* 液态金属外框（模糊 + 渐变流动） */}
        <div
          style={{
            position: 'absolute',
            inset: -18,
            borderRadius: 26,
            background: `conic-gradient(from ${(frame * 1.3) % 360}deg at 50% 50%,
              #f5f6fa 0deg,
              #8f95a8 60deg,
              #eef0f7 120deg,
              #6a7088 180deg,
              #f0f2f8 240deg,
              #8b91a6 300deg,
              #f5f6fa 360deg)`,
            filter: 'blur(0.4px)',
            boxShadow:
              '0 30px 80px rgba(0,0,0,0.55), 0 0 60px rgba(200,210,235,0.18)',
          }}
        />
        {/* 金属内嵌槽（深色凹陷） */}
        <div
          style={{
            position: 'absolute',
            inset: -6,
            borderRadius: 20,
            background:
              'linear-gradient(180deg, #1a1c22 0%, #0c0d11 100%)',
            boxShadow:
              'inset 0 2px 4px rgba(255,255,255,0.08), inset 0 -2px 4px rgba(0,0,0,0.6)',
          }}
        />

        {/* 画板内容（图片） */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 14,
            overflow: 'hidden',
          }}
        >
          {/* 上一张（渐隐） */}
          <Img
            src={images[prevIdx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 1 - fadeIn,
              filter: 'contrast(1.05) saturate(1.08)',
            }}
          />
          {/* 当前张（渐显） */}
          <Img
            src={images[idx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: fadeIn,
              filter: 'contrast(1.05) saturate(1.08)',
              transform: `scale(${1 + (1 - fadeIn) * 0.04})`,
            }}
          />

          {/* 彩虹油膜（覆盖层） */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(120deg,
                hsla(${hue}, 90%, 60%, 0.18) 0%,
                hsla(${(hue + 70) % 360}, 90%, 60%, 0.14) 35%,
                hsla(${(hue + 160) % 360}, 90%, 60%, 0.12) 65%,
                hsla(${(hue + 240) % 360}, 90%, 60%, 0.2) 100%)`,
              mixBlendMode: 'overlay',
              pointerEvents: 'none',
            }}
          />

          {/* 镜面扫光 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: sweep,
              width: 180,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
              filter: 'blur(6px)',
              mixBlendMode: 'screen',
              transform: 'skewX(-18deg)',
              pointerEvents: 'none',
            }}
          />

          {/* 涟漪环（中心爆开同心圆） */}
          {rippleRings.map((offset, i) => {
            const p = Math.max(0, Math.min(1, ripple - offset));
            if (p <= 0 || p >= 1) return null;
            const size = p * Math.max(panelW, panelH) * 1.6;
            const op = (1 - p) * 0.9;
            return (
              <div
                key={`lc-rp-${i}`}
                style={{
                  position: 'absolute',
                  left: panelW / 2 - size / 2,
                  top: panelH / 2 - size / 2,
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  border: `2px solid rgba(240,245,255,${op})`,
                  boxShadow: `0 0 ${20 + p * 30}px rgba(220,230,255,${op * 0.7}), inset 0 0 ${10 + p * 18}px rgba(200,220,255,${op * 0.5})`,
                  mixBlendMode: 'screen',
                  pointerEvents: 'none',
                }}
              />
            );
          })}

          {/* 顶部 / 底部 内阴影（给画面更大气的景深） */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 75%, rgba(0,0,0,0.35) 100%)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* 外框高光（上沿） */}
        <div
          style={{
            position: 'absolute',
            left: 10,
            right: 10,
            top: -12,
            height: 2,
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
            filter: 'blur(0.5px)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* 地板倒影（画板下方镜像） */}
      <div
        style={{
          position: 'absolute',
          left: panelX + panelW * 0.08,
          top: panelY + panelH + 12,
          width: panelW * 0.84,
          height: panelH * 0.32,
          overflow: 'hidden',
          transform: 'scaleY(-1)',
          maskImage:
            'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 100%)',
          opacity: ent * 0.55,
          filter: 'blur(1.2px)',
          pointerEvents: 'none',
        }}
      >
        <Img
          src={images[idx]}
          style={{
            width: '100%',
            height: panelH,
            objectFit: 'cover',
            filter: 'brightness(0.65) saturate(0.95)',
          }}
        />
      </div>

      {/* 顶部标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: ent,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 10,
            color: '#9aa3b8',
          }}
        >
          ◇ {subtitle} ◇
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 42,
            letterSpacing: 16,
            color: '#eef1fa',
            textShadow:
              '0 2px 18px rgba(200,210,235,0.35), 0 0 30px rgba(200,210,235,0.18)',
            fontWeight: 300,
            background:
              'linear-gradient(180deg, #f8f9fc 0%, #a8aebe 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {title}
        </div>
        <div
          style={{
            margin: '10px auto 0',
            width: 180,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, #9aa3b8, transparent)',
          }}
        />
      </div>

      {/* 左侧竖向索引 */}
      <div
        style={{
          position: 'absolute',
          left: 28,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 12,
          opacity: ent * 0.9,
        }}
      >
        {images.map((_, i) => (
          <div
            key={`lc-idx-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: '"Courier New", monospace',
              fontSize: 10,
              letterSpacing: 3,
              color: i === idx ? '#f0f3fb' : 'rgba(154,163,184,0.5)',
              transition: 'none',
            }}
          >
            <div
              style={{
                width: i === idx ? 30 : 12,
                height: 1.5,
                background:
                  i === idx
                    ? 'linear-gradient(90deg, #f0f3fb, rgba(240,243,251,0.2))'
                    : 'rgba(154,163,184,0.4)',
              }}
            />
            {String(i + 1).padStart(2, '0')}
          </div>
        ))}
      </div>

      {/* 底部 HUD */}
      <div
        style={{
          position: 'absolute',
          bottom: 28,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 46px',
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#9aa3b8',
          opacity: ent * 0.9,
        }}
      >
        <div>▸ SERIAL · HG-Ω-{String(idx + 1).padStart(3, '0')}</div>
        <div style={{ color: '#cfd4e2', letterSpacing: 8 }}>
          {labels?.[idx] ?? `PLATE · ${String(idx + 1).padStart(2, '0')} / ${String(N).padStart(2, '0')}`}
        </div>
        <div>ALLOY · MERCURY 99.97%</div>
      </div>

      {/* 全局扫描线（极轻） */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 3px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          opacity: 0.4,
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.75) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #7  ImageAuroraRibbonGallery —— 极光丝带画廊
// 特点：
// · 极地夜空背景：深蓝 → 紫 → 黑的大气渐变 + 繁星
// · 远处山脉剪影，地面有微弱雪原反射
// · 中央巨幅画框（完整展示图片），画框是冰蓝色金属 + 霜冻高光
// · 画框四周有多条"极光丝带"飘过（SVG 路径，颜色随时间流动）
// · 下落雪花 + 星辰闪烁
// · 每次切换，极光色带颜色变换 + 画框轻微缩放 + 图片淡入
// ---------------------------------------------------------------------
export const ImageAuroraRibbonGallery: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
  secondsPerImage?: number;
}> = ({
  images = DEFAULT_IMAGES_8,
  captions,
  title = 'AURORA · LUMEN',
  subtitle = 'NORTHERN CHAPTERS · MMXXVI',
  secondsPerImage = 2.4,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const N = images.length;
  const perShot = Math.max(24, Math.round(fps * secondsPerImage));
  const idx = Math.floor(frame / perShot) % N;
  const prevIdx = (idx - 1 + N) % N;
  const localFrame = frame - Math.floor(frame / perShot) * perShot;
  const fadeIn = interpolate(localFrame, [0, 18], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 画框
  const frameW = width * 0.66;
  const frameH = height * 0.72;
  const frameX = (width - frameW) / 2;
  const frameY = (height - frameH) / 2 - 4;

  // 入场
  const ent = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 110, mass: 0.85 },
  });

  // 极光色板（随 idx 变换）
  const auroraPalettes: Array<[string, string, string]> = [
    ['#54ffb8', '#36c0ff', '#b16bff'], // 青绿 / 湖蓝 / 紫
    ['#ff8fd8', '#9b7bff', '#3cf3ff'], // 粉 / 紫 / 青
    ['#9dff7a', '#36e0ff', '#ffd166'], // 黄绿 / 蓝 / 金
    ['#ff6fa3', '#ff9e3c', '#36c0ff'], // 洋红 / 橙 / 湖蓝
    ['#6affe0', '#6a9bff', '#c06bff'], // 薄荷 / 蓝 / 紫
    ['#ffd166', '#7cff9b', '#36c0ff'], // 金 / 翠绿 / 蓝
    ['#ff7ab6', '#c7ff6b', '#36e0ff'], // 粉 / 绿 / 蓝
    ['#a4ff9d', '#6a9bff', '#ff6fa3'], // 浅绿 / 蓝 / 粉
  ];
  const pal = auroraPalettes[idx % auroraPalettes.length];
  const prevPal = auroraPalettes[prevIdx % auroraPalettes.length];

  // 星辰
  const stars = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; tw: number }> = [];
    for (let i = 0; i < 160; i++) {
      arr.push({
        x: rand(i * 1.3),
        y: rand(i * 2.7) * 0.72,
        r: 0.4 + rand(i * 4.1) * 1.6,
        tw: rand(i * 5.5) * 6.28,
      });
    }
    return arr;
  }, []);

  // 雪花
  const snow = React.useMemo(() => {
    const arr: Array<{
      x: number;
      y: number;
      r: number;
      s: number;
      speed: number;
      sway: number;
    }> = [];
    for (let i = 0; i < 60; i++) {
      arr.push({
        x: rand(i * 7.7) * width,
        y: rand(i * 3.1) * height,
        r: 1 + rand(i * 5.5) * 2.4,
        s: i,
        speed: 0.4 + rand(i * 1.9) * 1.4,
        sway: rand(i * 2.3) * 4 + 2,
      });
    }
    return arr;
  }, [width, height]);

  // 极光丝带路径（3 条主丝带）
  const ribbonPaths = React.useMemo(() => {
    const arr: string[] = [];
    for (let k = 0; k < 3; k++) {
      const y0 = height * (0.18 + k * 0.06);
      const a1 = height * 0.12;
      const a2 = height * 0.08;
      const a3 = height * 0.14;
      const p = [
        `M -50 ${y0}`,
        `C ${width * 0.18} ${y0 - a1}, ${width * 0.32} ${y0 + a2}, ${width * 0.5} ${y0 - a3 * 0.6}`,
        `S ${width * 0.82} ${y0 + a2}, ${width + 80} ${y0 - 10}`,
      ].join(' ');
      arr.push(p);
    }
    return arr;
  }, [width, height]);

  // hue 漂移，让渐变更动态
  const flow = (frame * 0.8) % 100;

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #030418 0%, #0a0b2e 32%, #120a3c 55%, #1c0c48 72%, #05020e 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景辉光（极光散射） */}
      <div
        style={{
          position: 'absolute',
          left: '-10%',
          right: '-10%',
          top: '8%',
          height: '42%',
          background: `radial-gradient(ellipse at 28% 40%, ${pal[0]}55 0%, transparent 50%),
            radial-gradient(ellipse at 72% 30%, ${pal[1]}44 0%, transparent 55%),
            radial-gradient(ellipse at 50% 70%, ${pal[2]}33 0%, transparent 60%)`,
          filter: 'blur(36px)',
          opacity: 0.85,
          pointerEvents: 'none',
        }}
      />

      {/* 星空 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {stars.map((s, i) => {
          const a = 0.3 + 0.6 * (0.5 + 0.5 * Math.sin(frame / 14 + s.tw));
          return (
            <circle
              key={`ar-st-${i}`}
              cx={s.x * width}
              cy={s.y * height}
              r={s.r}
              fill="#e7ecff"
              opacity={a}
            />
          );
        })}
      </svg>

      {/* 极光丝带 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          {[0, 1, 2].map((k) => (
            <linearGradient
              key={`ar-grad-${k}`}
              id={`ar-grad-${k}`}
              x1="0"
              y1="0"
              x2="1"
              y2="0"
              gradientUnits="objectBoundingBox"
            >
              <stop offset={`${(0 + flow) % 100}%`} stopColor={pal[0]} stopOpacity="0" />
              <stop offset={`${(20 + flow) % 100}%`} stopColor={pal[0]} stopOpacity="0.8" />
              <stop offset={`${(50 + flow) % 100}%`} stopColor={pal[1]} stopOpacity="0.95" />
              <stop offset={`${(80 + flow) % 100}%`} stopColor={pal[2]} stopOpacity="0.75" />
              <stop offset="100%" stopColor={pal[2]} stopOpacity="0" />
            </linearGradient>
          ))}
          <filter id="ar-blur-soft" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <filter id="ar-blur-hard" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
        </defs>

        {/* 外层 发光 */}
        {ribbonPaths.map((d, k) => (
          <path
            key={`ar-rb-outer-${k}`}
            d={d}
            stroke={`url(#ar-grad-${k})`}
            strokeWidth={90 - k * 14}
            fill="none"
            opacity={0.55}
            filter="url(#ar-blur-hard)"
            transform={`translate(0 ${Math.sin(frame / 40 + k) * 6}) scale(1 ${1 + Math.sin(frame / 60 + k) * 0.05})`}
            style={{ transformOrigin: `${width / 2}px ${height / 2}px` }}
          />
        ))}
        {/* 中层 */}
        {ribbonPaths.map((d, k) => (
          <path
            key={`ar-rb-mid-${k}`}
            d={d}
            stroke={`url(#ar-grad-${k})`}
            strokeWidth={38 - k * 6}
            fill="none"
            opacity={0.85}
            filter="url(#ar-blur-soft)"
            transform={`translate(0 ${Math.sin(frame / 36 + k) * 4})`}
          />
        ))}
        {/* 亮线 */}
        {ribbonPaths.map((d, k) => (
          <path
            key={`ar-rb-core-${k}`}
            d={d}
            stroke="#e8fbff"
            strokeWidth={1.4 - k * 0.3}
            fill="none"
            opacity={0.7}
            transform={`translate(0 ${Math.sin(frame / 34 + k) * 3})`}
          />
        ))}
      </svg>

      {/* 远山剪影 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <polygon
          points={`0,${height * 0.82} ${width * 0.14},${height * 0.72} ${width * 0.24},${height * 0.78} ${width * 0.36},${height * 0.68} ${width * 0.5},${height * 0.8} ${width * 0.62},${height * 0.7} ${width * 0.76},${height * 0.78} ${width * 0.88},${height * 0.7} ${width},${height * 0.82} ${width},${height} 0,${height}`}
          fill="#03040d"
          opacity={0.96}
        />
        {/* 雪原反光（顶部 highlight） */}
        <polyline
          points={`0,${height * 0.82} ${width * 0.14},${height * 0.72} ${width * 0.24},${height * 0.78} ${width * 0.36},${height * 0.68} ${width * 0.5},${height * 0.8} ${width * 0.62},${height * 0.7} ${width * 0.76},${height * 0.78} ${width * 0.88},${height * 0.7} ${width},${height * 0.82}`}
          fill="none"
          stroke={pal[1]}
          strokeOpacity={0.35}
          strokeWidth={1.2}
        />
      </svg>

      {/* 地面雪原辉光（画框底部） */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: height * 0.22,
          background: `linear-gradient(180deg, transparent 0%, ${pal[1]}22 70%, ${prevPal[0]}15 100%)`,
          mixBlendMode: 'screen',
          pointerEvents: 'none',
        }}
      />

      {/* 雪花 */}
      {snow.map((f) => {
        const y = (f.y + frame * f.speed * 2) % height;
        const swayX = Math.sin(frame / 24 + f.s) * f.sway;
        return (
          <div
            key={`ar-sn-${f.s}`}
            style={{
              position: 'absolute',
              left: f.x + swayX,
              top: y,
              width: f.r,
              height: f.r,
              background: '#f0f6ff',
              opacity: 0.75,
              borderRadius: '50%',
              boxShadow: '0 0 4px rgba(220,235,255,0.6)',
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* 中央画框 */}
      <div
        style={{
          position: 'absolute',
          left: frameX,
          top: frameY,
          width: frameW,
          height: frameH,
          transform: `scale(${0.86 + ent * 0.14})`,
          opacity: ent,
        }}
      >
        {/* 外层光晕（极光色，紧贴画框） */}
        <div
          style={{
            position: 'absolute',
            inset: -32,
            borderRadius: 18,
            background: `linear-gradient(140deg, ${pal[0]}66, ${pal[1]}55, ${pal[2]}55)`,
            filter: 'blur(30px)',
            opacity: 0.75,
          }}
        />
        {/* 冰蓝金属画框 */}
        <div
          style={{
            position: 'absolute',
            inset: -14,
            borderRadius: 12,
            background:
              'linear-gradient(180deg, #dee8f5 0%, #8aa1be 40%, #617286 65%, #a4b7d1 100%)',
            boxShadow:
              '0 30px 80px rgba(0,0,0,0.6), 0 0 40px rgba(160,200,255,0.35), inset 0 2px 2px rgba(255,255,255,0.7)',
          }}
        />
        {/* 内阴影槽 */}
        <div
          style={{
            position: 'absolute',
            inset: -4,
            borderRadius: 6,
            background: '#0a0c1a',
            boxShadow:
              'inset 0 2px 4px rgba(0,0,0,0.7), inset 0 0 12px rgba(120,180,255,0.15)',
          }}
        />
        {/* 图像 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Img
            src={images[prevIdx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 1 - fadeIn,
            }}
          />
          <Img
            src={images[idx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: fadeIn,
              transform: `scale(${1 + (1 - fadeIn) * 0.03})`,
              filter: 'contrast(1.05) saturate(1.06)',
            }}
          />

          {/* 极光色调覆盖 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(180deg, ${pal[0]}22 0%, transparent 40%, transparent 70%, ${pal[2]}1f 100%)`,
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />
          {/* 霜冻角部 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 8% 8%, rgba(220,240,255,0.28) 0%, transparent 18%), radial-gradient(circle at 92% 92%, rgba(220,240,255,0.22) 0%, transparent 18%)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* 画框顶部高光 */}
        <div
          style={{
            position: 'absolute',
            left: 20,
            right: 20,
            top: -10,
            height: 1.5,
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
            pointerEvents: 'none',
          }}
        />
        {/* 画框四角装饰 */}
        {[
          { l: -20, t: -20, r: 'tl' },
          { r: -20, t: -20, r2: 'tr' },
          { l: -20, b: -20, r3: 'bl' },
          { r: -20, b: -20, r4: 'br' },
        ].map((c, k) => (
          <div
            key={`ar-corner-${k}`}
            style={{
              position: 'absolute',
              left: 'l' in c ? c.l : undefined,
              top: 't' in c ? c.t : undefined,
              right: 'r' in c ? c.r : undefined,
              bottom: 'b' in c ? c.b : undefined,
              width: 20,
              height: 20,
              border: `1.5px solid ${pal[1]}`,
              borderRadius: 3,
              boxShadow: `0 0 12px ${pal[1]}88`,
              opacity: 0.85,
            }}
          />
        ))}
      </div>

      {/* 顶部标题（北极风） */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: ent,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 10,
            color: pal[0],
            textShadow: `0 0 10px ${pal[0]}aa`,
          }}
        >
          ◇ {subtitle} ◇
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 46,
            letterSpacing: 14,
            color: '#eaf4ff',
            fontWeight: 300,
            textShadow: `0 0 18px ${pal[1]}aa, 0 0 36px ${pal[2]}55`,
          }}
        >
          {title}
        </div>
        <div
          style={{
            margin: '10px auto 0',
            width: 200,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${pal[1]}, ${pal[2]}, transparent)`,
          }}
        />
      </div>

      {/* 底部 hud */}
      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: ent * 0.95,
        }}
      >
        <div
          style={{
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 20,
            letterSpacing: 8,
            color: '#eaf4ff',
            fontStyle: 'italic',
          }}
        >
          {captions?.[idx] ?? `Chapter ${idx + 1} · Song of the Northern Sky`}
        </div>
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          {images.map((_, i) => (
            <div
              key={`ar-pb-${i}`}
              style={{
                width: i === idx ? 36 : 10,
                height: 2,
                background:
                  i === idx
                    ? `linear-gradient(90deg, ${pal[0]}, ${pal[2]})`
                    : 'rgba(180,200,230,0.25)',
                boxShadow:
                  i === idx ? `0 0 10px ${pal[1]}cc` : undefined,
                borderRadius: 2,
              }}
            />
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 6,
            color: 'rgba(200,215,240,0.55)',
          }}
        >
          LAT 78°13′N · LON 15°38′E · SVALBARD · MMXXVI
        </div>
      </div>

      {/* 全局颗粒 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(255,255,255,0.015) 0 1px, transparent 1px 3px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          opacity: 0.5,
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.72) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #8  ImageVolumetricStageSpotlight —— 体积光剧场
// 特点：
// · 黑色剧场内景 + 暗红色幕布轮廓 + 反光地板
// · 中央一块大画框完整展示图片（约 65% 宽）
// · 画框上方 3 束体积光（聚光灯）投射下来，光柱内有烟雾 / 粒子流动
// · 每次切换，光柱颜色从暖金切换到冷蓝 / 品红 / 翠绿等；
// · 画框在光柱中"被点亮"，周围黑暗处仅有弱红幕布光
// · 地板上有画框 + 光柱的倒影
// · 顶部舞台拱顶 + 侧边 "ACT · SCENE" HUD
// ---------------------------------------------------------------------
export const ImageVolumetricStageSpotlight: React.FC<{
  images?: string[];
  actTitles?: string[];
  title?: string;
  subtitle?: string;
  theater?: string;
  secondsPerImage?: number;
}> = ({
  images = DEFAULT_IMAGES_8,
  actTitles,
  title = 'THEATRVM · LVMEN',
  subtitle = 'A PLAY IN EIGHT ACTS',
  theater = 'THE GRAND HALL · MMXXVI',
  secondsPerImage = 2.2,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  const N = images.length;
  const perShot = Math.max(24, Math.round(fps * secondsPerImage));
  const idx = Math.floor(frame / perShot) % N;
  const prevIdx = (idx - 1 + N) % N;
  const localFrame = frame - Math.floor(frame / perShot) * perShot;
  const fadeIn = interpolate(localFrame, [0, 16], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 画框
  const panelW = width * 0.58;
  const panelH = height * 0.62;
  const panelX = (width - panelW) / 2;
  const panelY = height * 0.24;

  // 入场：幕布上升
  const curtainRise = spring({
    frame,
    fps,
    config: { damping: 24, stiffness: 90, mass: 1.2 },
  });
  const ent = spring({
    frame: frame - 18,
    fps,
    config: { damping: 20, stiffness: 110, mass: 0.8 },
  });

  // 光柱颜色板
  const beamPalettes: Array<[string, string]> = [
    ['#ffd89a', '#ff7a3c'], // 金 / 暖橙
    ['#8fd6ff', '#3c7aff'], // 冰蓝 / 深蓝
    ['#ff9bd5', '#c43cff'], // 粉 / 品红
    ['#9dffc8', '#1fc47a'], // 薄荷 / 翠绿
    ['#fff3a0', '#ffb63c'], // 柠黄 / 琥珀
    ['#ff8a8a', '#c4223c'], // 珊瑚 / 红
    ['#c6a7ff', '#6a3cff'], // 淡紫 / 深紫
    ['#a0f7ff', '#28c2d8'], // 青 / 湖
  ];
  const pal = beamPalettes[idx % beamPalettes.length];
  const prevPal = beamPalettes[prevIdx % beamPalettes.length];

  // 烟雾粒子
  const smoke = React.useMemo(() => {
    const arr: Array<{
      x: number;
      y: number;
      r: number;
      s: number;
      speed: number;
      drift: number;
    }> = [];
    for (let i = 0; i < 70; i++) {
      arr.push({
        x: rand(i * 1.7),
        y: rand(i * 3.3),
        r: 18 + rand(i * 5.1) * 36,
        s: i,
        speed: 0.3 + rand(i * 2.3) * 0.6,
        drift: rand(i * 4.7) * 30 + 20,
      });
    }
    return arr;
  }, []);

  // 光柱尘粒
  const dust = React.useMemo(() => {
    const arr: Array<{
      x: number;
      y: number;
      r: number;
      s: number;
      speed: number;
    }> = [];
    for (let i = 0; i < 120; i++) {
      arr.push({
        x: rand(i * 1.9),
        y: rand(i * 3.1),
        r: 1 + rand(i * 5.3) * 2,
        s: i,
        speed: 0.4 + rand(i * 2.1) * 1,
      });
    }
    return arr;
  }, []);

  // 3 束光柱的水平锚点（x，以画面宽度比例）
  const beamAnchors = [0.32, 0.5, 0.68];

  // 地板线（反光）
  const floorY = height * 0.87;

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 42%, #1a0f13 0%, #0a0508 60%, #020103 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 舞台顶 拱 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="vs-arch" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a0e14" />
            <stop offset="100%" stopColor="#0b0305" />
          </linearGradient>
          <linearGradient id="vs-curtain-l" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1a0609" />
            <stop offset="60%" stopColor="#48111a" />
            <stop offset="100%" stopColor="#2a0a10" />
          </linearGradient>
          <linearGradient id="vs-curtain-r" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#1a0609" />
            <stop offset="60%" stopColor="#48111a" />
            <stop offset="100%" stopColor="#2a0a10" />
          </linearGradient>
        </defs>
        {/* 拱顶 */}
        <path
          d={`M 0 0 L ${width} 0 L ${width} ${height * 0.18} Q ${width / 2} ${height * 0.06} 0 ${height * 0.18} Z`}
          fill="url(#vs-arch)"
          opacity={0.92}
        />
        {/* 拱顶金线 */}
        <path
          d={`M 0 ${height * 0.18} Q ${width / 2} ${height * 0.06} ${width} ${height * 0.18}`}
          stroke="#b88a3d"
          strokeWidth={1.2}
          fill="none"
          opacity={0.6}
        />
        {/* 拱顶镂空花纹 */}
        {Array.from({ length: 13 }).map((_, i) => {
          const t = (i + 0.5) / 13;
          const x = t * width;
          const y = height * 0.18 - Math.sin(t * Math.PI) * height * 0.12 + 8;
          return (
            <g key={`vs-orn-${i}`} opacity={0.5}>
              <circle cx={x} cy={y} r={2.2} fill="#d8a24a" />
            </g>
          );
        })}
      </svg>

      {/* 左右幕布（入场上拉） */}
      {[
        {
          side: 'left' as const,
          w: Math.max(width * (0.26 - curtainRise * 0.1), width * 0.12),
        },
        {
          side: 'right' as const,
          w: Math.max(width * (0.26 - curtainRise * 0.1), width * 0.12),
        },
      ].map((c) => (
        <div
          key={`vs-cur-${c.side}`}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            [c.side]: 0,
            width: c.w,
            background: `linear-gradient(${c.side === 'left' ? '90deg' : '-90deg'},
              #2a0a10 0%, #48111a 40%, #6a1b28 70%, #2e0b12 100%)`,
            boxShadow:
              c.side === 'left'
                ? 'inset -40px 0 60px rgba(0,0,0,0.75)'
                : 'inset 40px 0 60px rgba(0,0,0,0.75)',
          }}
        >
          {/* 幕布褶皱 */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={`vs-cur-fold-${c.side}-${i}`}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                [c.side === 'left' ? 'left' : 'right']: `${i * 12 + 4}%`,
                width: 2,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.5), rgba(0,0,0,0))',
                opacity: 0.7,
              }}
            />
          ))}
          {/* 金色流苏底部 */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 14,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(184,138,61,0.65) 100%)',
            }}
          />
        </div>
      ))}

      {/* 顶部打光圆环（吊灯） */}
      {[0.28, 0.5, 0.72].map((xf, i) => (
        <div
          key={`vs-lamp-${i}`}
          style={{
            position: 'absolute',
            left: xf * width - 12,
            top: height * 0.08,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${pal[0]} 0%, ${pal[1]}66 60%, transparent 100%)`,
            boxShadow: `0 0 30px ${pal[0]}aa, 0 0 60px ${pal[1]}55`,
            opacity: 0.9 * ent,
            zIndex: 50,
          }}
        />
      ))}

      {/* 三束体积光（SVG 梯形渐变） */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          mixBlendMode: 'screen',
          opacity: ent * 0.95,
        }}
      >
        <defs>
          {beamAnchors.map((xf, i) => (
            <linearGradient
              key={`vs-beam-grad-${i}`}
              id={`vs-beam-grad-${i}`}
              x1="0.5"
              y1="0"
              x2="0.5"
              y2="1"
            >
              <stop offset="0%" stopColor={pal[0]} stopOpacity="0.9" />
              <stop offset="25%" stopColor={pal[0]} stopOpacity="0.6" />
              <stop offset="60%" stopColor={pal[1]} stopOpacity="0.35" />
              <stop offset="100%" stopColor={pal[1]} stopOpacity="0" />
            </linearGradient>
          ))}
          <filter id="vs-beam-blur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>
        {beamAnchors.map((xf, i) => {
          const topX = xf * width;
          const flicker = 0.85 + Math.abs(Math.sin(frame / 7 + i * 2)) * 0.15;
          const swing = Math.sin(frame / 45 + i * 1.5) * 24;
          return (
            <g key={`vs-beam-${i}`} opacity={flicker}>
              <polygon
                points={`${topX - 14},${height * 0.1} ${topX + 14},${height * 0.1} ${topX + 180 + swing},${floorY} ${topX - 180 + swing},${floorY}`}
                fill={`url(#vs-beam-grad-${i})`}
                filter="url(#vs-beam-blur)"
              />
              <polygon
                points={`${topX - 6},${height * 0.1} ${topX + 6},${height * 0.1} ${topX + 110 + swing},${floorY} ${topX - 110 + swing},${floorY}`}
                fill={`url(#vs-beam-grad-${i})`}
                opacity={0.5}
              />
            </g>
          );
        })}
      </svg>

      {/* 光柱尘粒（在光柱区域内随机漂浮） */}
      {dust.map((d) => {
        const y = (d.y + frame / 200) % 1;
        const t = y; // 0→1 从上至下
        const bi = Math.floor(d.s / 40) % 3;
        const baseX = beamAnchors[bi] * width;
        const spread = (14 + t * 220) * (d.x - 0.5) * 2;
        const swing = Math.sin(frame / 45 + bi * 1.5) * 24 * t;
        const absX = baseX + spread + swing;
        const absY = height * 0.1 + t * (floorY - height * 0.1);
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame / 8 + d.s));
        return (
          <div
            key={`vs-dust-${d.s}`}
            style={{
              position: 'absolute',
              left: absX,
              top: absY,
              width: d.r,
              height: d.r,
              background: pal[0],
              opacity: tw * 0.55 * (1 - t * 0.3),
              borderRadius: '50%',
              boxShadow: `0 0 6px ${pal[0]}`,
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* 烟雾（光柱中飘过的大 blob） */}
      {smoke.map((s) => {
        const yBase = (s.y + frame / 400) % 1;
        const absY = height * 0.14 + yBase * (floorY - height * 0.18);
        const absX = s.x * width + Math.sin(frame / 50 + s.s) * s.drift;
        const tw = 0.12 + 0.1 * Math.abs(Math.sin(frame / 22 + s.s));
        return (
          <div
            key={`vs-smk-${s.s}`}
            style={{
              position: 'absolute',
              left: absX - s.r,
              top: absY - s.r,
              width: s.r * 2,
              height: s.r * 2,
              background: `radial-gradient(circle, ${pal[0]}99 0%, ${pal[1]}33 40%, transparent 70%)`,
              opacity: tw,
              filter: 'blur(8px)',
              mixBlendMode: 'screen',
              pointerEvents: 'none',
              borderRadius: '50%',
            }}
          />
        );
      })}

      {/* 中央画框 */}
      <div
        style={{
          position: 'absolute',
          left: panelX,
          top: panelY,
          width: panelW,
          height: panelH,
          transform: `scale(${0.88 + ent * 0.12})`,
          opacity: ent,
        }}
      >
        {/* 画框柔光 */}
        <div
          style={{
            position: 'absolute',
            inset: -28,
            borderRadius: 14,
            background: `radial-gradient(ellipse at 50% 0%, ${pal[0]}aa 0%, ${pal[1]}55 30%, transparent 70%)`,
            filter: 'blur(20px)',
            opacity: 0.8,
          }}
        />
        {/* 金属描边框（拉丝金） */}
        <div
          style={{
            position: 'absolute',
            inset: -10,
            borderRadius: 8,
            background:
              'linear-gradient(180deg, #f0d89a 0%, #b58845 40%, #8a6027 65%, #e6c874 100%)',
            boxShadow:
              '0 20px 70px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.4), inset 0 1.5px 1.5px rgba(255,255,255,0.5)',
          }}
        />
        {/* 内槽 */}
        <div
          style={{
            position: 'absolute',
            inset: -2,
            borderRadius: 4,
            background: '#000',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.9)',
          }}
        />
        {/* 图片 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          <Img
            src={images[prevIdx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 1 - fadeIn,
              filter: 'brightness(0.9)',
            }}
          />
          <Img
            src={images[idx]}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: fadeIn,
              transform: `scale(${1 + (1 - fadeIn) * 0.03})`,
              filter: 'contrast(1.08) saturate(1.08)',
            }}
          />

          {/* 聚光灯色温覆盖 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(ellipse at 50% 0%, ${pal[0]}33 0%, transparent 60%),
                radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 60%)`,
              pointerEvents: 'none',
              mixBlendMode: 'overlay',
            }}
          />
        </div>

        {/* 画框上沿高光 */}
        <div
          style={{
            position: 'absolute',
            left: 14,
            right: 14,
            top: -8,
            height: 1.5,
            background:
              'linear-gradient(90deg, transparent, rgba(255,240,210,0.95), transparent)',
            pointerEvents: 'none',
          }}
        />

        {/* 画框下方 装饰牌 */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: -50,
            padding: '6px 24px',
            background:
              'linear-gradient(180deg, #2a1a0c 0%, #140a05 100%)',
            border: '1px solid #8a6027',
            boxShadow: `0 4px 14px rgba(0,0,0,0.6), 0 0 18px ${pal[0]}66`,
            borderRadius: 3,
          }}
        >
          <div
            style={{
              fontFamily:
                '"Didot","Playfair Display","Georgia",serif',
              fontSize: 14,
              letterSpacing: 8,
              color: '#e6c874',
              fontStyle: 'italic',
              whiteSpace: 'nowrap',
            }}
          >
            {actTitles?.[idx] ?? `ACT ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'][idx] ?? idx + 1} · Scene ${String(idx + 1).padStart(2, '0')}`}
          </div>
        </div>
      </div>

      {/* 地板（反光） */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: floorY,
          bottom: 0,
          background:
            'linear-gradient(180deg, #0b0609 0%, #1a0e11 30%, #050203 100%)',
          overflow: 'hidden',
        }}
      >
        {/* 地板反射光 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: '100%',
            background: `radial-gradient(ellipse at 50% 0%, ${pal[0]}33 0%, ${pal[1]}11 30%, transparent 60%)`,
            mixBlendMode: 'screen',
          }}
        />
        {/* 地板木纹线 */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={`vs-floor-line-${i}`}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${i * 14 + 4}%`,
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(180,140,90,0.15), transparent)',
            }}
          />
        ))}
      </div>

      {/* 画框地板倒影 */}
      <div
        style={{
          position: 'absolute',
          left: panelX + panelW * 0.06,
          top: floorY + 2,
          width: panelW * 0.88,
          height: (height - floorY) * 1.4,
          overflow: 'hidden',
          transform: 'scaleY(-1)',
          transformOrigin: 'top',
          maskImage:
            'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
          opacity: ent * 0.4,
          filter: 'blur(1.4px)',
          pointerEvents: 'none',
        }}
      >
        <Img
          src={images[idx]}
          style={{
            width: '100%',
            height: panelH,
            objectFit: 'cover',
            filter: 'brightness(0.55) saturate(0.9)',
          }}
        />
      </div>

      {/* 顶部 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 120,
          opacity: ent,
        }}
      >
        <div
          style={{
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 10,
            color: '#c9a260',
          }}
        >
          ◆ {theater} ◆
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily:
              '"Didot","Playfair Display","Georgia",serif',
            fontSize: 40,
            letterSpacing: 14,
            color: '#f5e6c8',
            textShadow:
              '0 0 20px rgba(245,230,200,0.35), 0 0 40px rgba(255,216,154,0.25)',
            fontWeight: 400,
          }}
        >
          {title}
        </div>
        <div
          style={{
            margin: '8px auto 0',
            width: 220,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, #c9a260, transparent)',
          }}
        />
        <div
          style={{
            marginTop: 6,
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            letterSpacing: 8,
            color: '#8a6f3f',
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 左右侧 HUD */}
      <div
        style={{
          position: 'absolute',
          left: 28,
          bottom: 22,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#c9a260',
          opacity: ent * 0.85,
        }}
      >
        ▸ HOUSE LIGHTS · DIM
        <br />
        ▸ SPOT · {pal[0].toUpperCase()}
        <br />
        ▸ CUE {String(idx + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
      </div>
      <div
        style={{
          position: 'absolute',
          right: 28,
          bottom: 22,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          letterSpacing: 4,
          color: '#c9a260',
          opacity: ent * 0.85,
          textAlign: 'right',
        }}
      >
        ROW A · SEAT 07 ◂
        <br />
        {String(Math.floor(frame / fps / 60)).padStart(2, '0')}:
        {String(Math.floor((frame / fps) % 60)).padStart(2, '0')} RUNNING
        <br />
        PREVIOUS · {prevPal[0].toUpperCase()}
      </div>

      {/* 画框上方灰尘雾（加强立体感） */}
      <div
        style={{
          position: 'absolute',
          left: panelX - 40,
          right: width - panelX - panelW - 40,
          top: height * 0.08,
          height: panelY - height * 0.08 + 30,
          background: `linear-gradient(180deg, transparent 0%, ${pal[0]}11 60%, ${pal[0]}22 100%)`,
          pointerEvents: 'none',
          mixBlendMode: 'screen',
          filter: 'blur(14px)',
          opacity: ent,
        }}
      />

      {/* 整体颗粒 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(0deg, rgba(255,240,210,0.02) 0 1px, transparent 1px 2px)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          opacity: 0.5,
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 48%, rgba(0,0,0,0.82) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #9  ImagePastelDreamBubbles —— 马卡龙梦境泡泡
// 特点：
// · 柔软马卡龙粉彩渐变背景（粉→奶油→薰衣草紫），上方漂浮云朵
// · 5 个圆形泡泡，每个泡泡包裹一张图片
// · 泡泡有柔光高光、彩虹油膜折射、缓慢上下浮动
// · 围绕泡泡漂浮爱心、星星、彩虹弧、小气泡
// · 手写可爱风标题 + 笑脸装饰 + 虚线连接泡泡
// ---------------------------------------------------------------------
export const ImagePastelDreamBubbles: React.FC<{
  images?: string[];
  labels?: string[];
  title?: string;
  subtitle?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  labels = ['Sweet', 'Happy', 'Dreamy', 'Cozy', 'Lovely'],
  title = 'Daydream',
  subtitle = '~ a pocket of soft moments ~',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 5 个泡泡 —— 错落摆放的可爱布局
  const bubbleN = 5;
  const layout = [
    { cx: 0.22, cy: 0.58, r: 0.16 },
    { cx: 0.40, cy: 0.34, r: 0.14 },
    { cx: 0.55, cy: 0.62, r: 0.18 },
    { cx: 0.72, cy: 0.32, r: 0.13 },
    { cx: 0.84, cy: 0.60, r: 0.15 },
  ];

  // 马卡龙色系
  const macaron = [
    { rim: '#ffb6c6', glow: '#ffdfe9', tint: '#fff0f3' }, // 草莓粉
    { rim: '#a9dfff', glow: '#d8f1ff', tint: '#eaf7ff' }, // 天空蓝
    { rim: '#ffd79a', glow: '#ffeac8', tint: '#fff5e1' }, // 奶油黄
    { rim: '#c4b8ff', glow: '#e3dcff', tint: '#f2eeff' }, // 薰衣草紫
    { rim: '#b8ecc8', glow: '#dff6e5', tint: '#f0fbf3' }, // 抹茶绿
  ];

  const headerOp = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 漂浮小装饰（爱心 / 星星 / 小泡泡）
  const decorations = React.useMemo(() => {
    type D = {
      x: number;
      y: number;
      type: 'heart' | 'star' | 'bubble' | 'sparkle';
      size: number;
      hue: number;
      speed: number;
      seed: number;
    };
    const arr: D[] = [];
    const types: D['type'][] = ['heart', 'star', 'bubble', 'sparkle'];
    for (let i = 0; i < 42; i++) {
      arr.push({
        x: rand(i * 2.1) * width,
        y: rand(i * 3.7) * height,
        type: types[Math.floor(rand(i * 7.7) * 4)],
        size: 8 + rand(i * 1.3) * 18,
        hue: Math.floor(rand(i * 5.9) * 360),
        speed: 0.3 + rand(i * 4.4) * 0.8,
        seed: i,
      });
    }
    return arr;
  }, [width, height]);

  // 云朵
  const clouds = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; s: number; seed: number }> = [];
    for (let i = 0; i < 6; i++) {
      arr.push({
        x: rand(i * 9.1) * width,
        y: 40 + rand(i * 2.3) * (height * 0.35),
        s: 0.7 + rand(i * 1.7) * 0.8,
        seed: i,
      });
    }
    return arr;
  }, [width, height]);

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #ffe6ee 0%, #fff1dc 35%, #ecdcff 75%, #d9ecff 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 背景点状纹理 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, opacity: 0.35 }}
      >
        {Array.from({ length: 90 }).map((_, i) => (
          <circle
            key={`dot-${i}`}
            cx={rand(i * 1.2) * width}
            cy={rand(i * 3.1) * height}
            r={1 + rand(i * 5.3) * 1.6}
            fill={`hsla(${Math.floor(rand(i * 7.7) * 360)},80%,80%,0.6)`}
          />
        ))}
      </svg>

      {/* 远处云朵（飘动） */}
      {clouds.map((c) => {
        const drift = ((frame * 0.35 + c.seed * 100) % (width + 400)) - 200;
        const cx = ((c.x + drift) % (width + 200)) - 100;
        const bob = Math.sin(frame / 50 + c.seed) * 6;
        return (
          <div
            key={`cloud-${c.seed}`}
            style={{
              position: 'absolute',
              left: cx,
              top: c.y + bob,
              opacity: 0.8,
              transform: `scale(${c.s})`,
            }}
          >
            <svg width="160" height="60" viewBox="0 0 160 60">
              <g fill="rgba(255,255,255,0.85)">
                <ellipse cx="40" cy="40" rx="32" ry="18" />
                <ellipse cx="70" cy="28" rx="26" ry="22" />
                <ellipse cx="100" cy="34" rx="30" ry="20" />
                <ellipse cx="128" cy="42" rx="24" ry="16" />
              </g>
              <g fill="rgba(255,220,230,0.65)">
                <circle cx="58" cy="36" r="3" />
                <circle cx="68" cy="36" r="3" />
                <path
                  d="M 58 42 Q 63 46 68 42"
                  stroke="rgba(255,140,170,0.7)"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </svg>
          </div>
        );
      })}

      {/* 顶部手写标题 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 36,
          textAlign: 'center',
          opacity: headerOp,
          transform: `translateY(${(1 - headerOp) * -20}px)`,
        }}
      >
        <div
          style={{
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", "Segoe Script", cursive',
            fontSize: 72,
            color: '#ff7ea4',
            letterSpacing: 1,
            textShadow:
              '2px 3px 0 #fff, 4px 6px 0 rgba(255,180,200,0.5)',
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span style={{ fontSize: 42, transform: 'translateY(-6px)' }}>✿</span>
          {title}
          <span style={{ fontSize: 42, transform: 'translateY(-6px)' }}>✿</span>
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
            fontSize: 22,
            color: '#b48cc8',
            letterSpacing: 2,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 5 个泡泡 */}
      {layout.map((b, i) => {
        const pal = macaron[i % macaron.length];
        const cx = b.cx * width;
        const cy = b.cy * height;
        const radius = b.r * Math.min(width, height);

        // 从下方漂浮进入 + spring
        const enter = spring({
          frame: frame - 18 - i * 9,
          fps,
          config: { damping: 12, stiffness: 90, mass: 1 },
        });
        const enterY = interpolate(enter, [0, 1], [260, 0]);
        const enterOp = interpolate(frame - 18 - i * 9, [0, 14], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 持续浮动
        const bob = Math.sin((frame + i * 22) / 26) * 7;
        const wobble = Math.sin((frame + i * 17) / 34) * 2.2;

        // 进入后图片显现
        const imgShow = interpolate(frame - 30 - i * 9, [0, 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        // 泡泡高光绕转
        const hiAng = (frame + i * 30) * 1.8;

        return (
          <div
            key={`bubble-${i}`}
            style={{
              position: 'absolute',
              left: cx - radius,
              top: cy - radius + enterY,
              width: radius * 2,
              height: radius * 2,
              opacity: enterOp,
              transform: `translateY(${bob}px) rotate(${wobble}deg)`,
              zIndex: 10 + i,
            }}
          >
            {/* 泡泡阴影 */}
            <div
              style={{
                position: 'absolute',
                left: '10%',
                right: '10%',
                bottom: -14,
                height: 24,
                background:
                  'radial-gradient(ellipse at center, rgba(180,120,150,0.35) 0%, transparent 70%)',
                filter: 'blur(6px)',
              }}
            />

            {/* 泡泡外环（彩虹折射） */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: `conic-gradient(from ${hiAng}deg, ${pal.rim}, #fff0f6, ${pal.rim}, #f6e5ff, ${pal.rim})`,
                filter: 'blur(2px)',
                opacity: 0.75,
              }}
            />

            {/* 泡泡主体（玻璃质感） */}
            <div
              style={{
                position: 'absolute',
                inset: 4,
                borderRadius: '50%',
                background: `radial-gradient(circle at 32% 28%, #ffffff 0%, ${pal.tint} 28%, ${pal.glow} 62%, ${pal.rim} 100%)`,
                boxShadow: `0 12px 30px ${pal.rim}99, inset 0 0 0 3px rgba(255,255,255,0.55), inset 0 -12px 30px rgba(255,255,255,0.4)`,
                overflow: 'hidden',
              }}
            >
              {/* 图片（圆形裁剪） */}
              <div
                style={{
                  position: 'absolute',
                  inset: '14%',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  opacity: imgShow,
                  transform: `scale(${0.86 + imgShow * 0.14})`,
                  boxShadow:
                    'inset 0 0 0 3px rgba(255,255,255,0.7), 0 6px 14px rgba(200,120,160,0.35)',
                }}
              >
                <Img
                  src={images[i % images.length]}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(1.1) brightness(1.05)',
                  }}
                />
                {/* 图片内淡化粉彩蒙 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at 50% 30%, transparent 55%, ${pal.rim}44 100%)`,
                  }}
                />
              </div>

              {/* 顶部圆光高光 */}
              <div
                style={{
                  position: 'absolute',
                  left: '18%',
                  top: '10%',
                  width: '28%',
                  height: '22%',
                  borderRadius: '50%',
                  background:
                    'radial-gradient(ellipse at center, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)',
                  filter: 'blur(1px)',
                }}
              />
              {/* 小圆光点 */}
              <div
                style={{
                  position: 'absolute',
                  right: '22%',
                  top: '18%',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  filter: 'blur(0.5px)',
                }}
              />

              {/* 油膜折射薄光 */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: `conic-gradient(from ${-hiAng}deg, transparent 0deg, rgba(255,220,255,0.18) 90deg, transparent 180deg, rgba(200,240,255,0.2) 270deg, transparent 360deg)`,
                  mixBlendMode: 'screen',
                }}
              />
            </div>

            {/* 泡泡下方标签 */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '100%',
                marginTop: 18,
                textAlign: 'center',
                opacity: imgShow,
                fontFamily:
                  '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
                fontSize: 22,
                color: '#86588c',
                letterSpacing: 1,
              }}
            >
              · {labels[i % labels.length]} ·
            </div>
          </div>
        );
      })}

      {/* 泡泡之间的虚线连线（手绘感） */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: interpolate(frame, [60, 100], [0, 0.6], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        {layout.map((b, i) => {
          if (i === layout.length - 1) return null;
          const a = layout[i];
          const nb = layout[i + 1];
          const x1 = a.cx * width;
          const y1 = a.cy * height;
          const x2 = nb.cx * width;
          const y2 = nb.cy * height;
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2 - 40;
          return (
            <path
              key={`line-${i}`}
              d={`M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`}
              stroke="#d089a8"
              strokeWidth={2}
              strokeDasharray="3 8"
              fill="none"
              strokeLinecap="round"
              opacity={0.6}
            />
          );
        })}
      </svg>

      {/* 漂浮装饰元素 */}
      {decorations.map((d) => {
        const life = ((frame * d.speed + d.seed * 12) / 90) % 1;
        const y = d.y - life * 160;
        const op =
          life < 0.1
            ? life * 10
            : life > 0.85
              ? (1 - life) * 7
              : 1;
        const rot = (frame + d.seed * 9) * (d.type === 'star' ? 1.2 : 0.6);
        const pastelColor = `hsl(${d.hue},70%,75%)`;
        return (
          <div
            key={`dec-${d.seed}`}
            style={{
              position: 'absolute',
              left: d.x,
              top: y,
              width: d.size,
              height: d.size,
              opacity: op * 0.85,
              transform: `rotate(${rot}deg)`,
              color: pastelColor,
              pointerEvents: 'none',
            }}
          >
            {d.type === 'heart' && (
              <svg viewBox="0 0 24 24" width={d.size} height={d.size}>
                <path
                  d="M12 21s-7-4.5-9.5-9.5C.5 7 4 3 8 4.5 10 5.25 11 6.5 12 8c1-1.5 2-2.75 4-3.5 4-1.5 7.5 2.5 5.5 7C19 16.5 12 21 12 21z"
                  fill={pastelColor}
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth="1"
                />
              </svg>
            )}
            {d.type === 'star' && (
              <svg viewBox="0 0 24 24" width={d.size} height={d.size}>
                <path
                  d="M12 2l2.6 6.8 7.4.6-5.6 4.9 1.7 7.2L12 17.7 5.9 21.5l1.7-7.2L2 9.4l7.4-.6z"
                  fill={pastelColor}
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="1"
                />
              </svg>
            )}
            {d.type === 'bubble' && (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.85), ${pastelColor}AA 70%, transparent 100%)`,
                  boxShadow: `inset 0 0 0 1.5px rgba(255,255,255,0.5)`,
                }}
              />
            )}
            {d.type === 'sparkle' && (
              <svg viewBox="0 0 24 24" width={d.size} height={d.size}>
                <path
                  d="M12 2 L13 10 L22 12 L13 14 L12 22 L11 14 L2 12 L11 10 Z"
                  fill={pastelColor}
                  opacity="0.9"
                />
              </svg>
            )}
          </div>
        );
      })}

      {/* 右下角可爱签名印章 */}
      <div
        style={{
          position: 'absolute',
          right: 32,
          bottom: 28,
          opacity: headerOp * 0.9,
          transform: 'rotate(-6deg)',
          fontFamily:
            '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
          fontSize: 18,
          color: '#8a5574',
          background: '#fff4f8',
          padding: '6px 14px',
          borderRadius: 16,
          border: '2px dashed #e0a9bf',
          boxShadow: '2px 3px 0 rgba(200,130,160,0.3)',
        }}
      >
        ♡ made with love ♡
      </div>

      {/* 底部装饰文字 */}
      <div
        style={{
          position: 'absolute',
          left: 32,
          bottom: 32,
          opacity: headerOp * 0.8,
          fontFamily:
            '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
          fontSize: 16,
          color: '#a080a8',
          letterSpacing: 2,
        }}
      >
        ୨୧ {new Date().getFullYear()} · soft hours diary
      </div>

      {/* 柔和暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 60%, rgba(255,200,220,0.35) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #10 ImageHandmadeScrapbook —— 手作剪贴簿
// 特点：
// · 温暖米黄色网格牛皮纸背景，边缘有咖啡渍/手绘涂鸦
// · 5 张图片以不同角度摆放，像照片贴在笔记本里
// · 每张照片用彩色胶带 (washi tape) 粘住，有条纹/波点花纹
// · 旁边散落手写文字、箭头、爱心贴纸、小花 / 叶子 / 星星贴纸
// · 顶部手写标题 + 日期戳印 + 回形针装饰
// · 入场：照片依次"啪"地贴上，带轻微弹跳
// ---------------------------------------------------------------------
export const ImageHandmadeScrapbook: React.FC<{
  images?: string[];
  captions?: string[];
  title?: string;
  subtitle?: string;
  dateStamp?: string;
}> = ({
  images = DEFAULT_IMAGES_8,
  captions = [
    'sunny afternoon ♡',
    'little adventure!',
    'cozy tea time ~',
    'hello little world',
    'best day ever ☆',
  ],
  title = 'My Little Diary',
  subtitle = 'a handmade collection of soft things',
  dateStamp = 'APR · 2026',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 5 张照片的手工布局
  const photos = [
    { cx: 0.24, cy: 0.48, w: 0.22, h: 0.30, rot: -6, tapeColor: 0 },
    { cx: 0.48, cy: 0.36, w: 0.20, h: 0.26, rot: 4, tapeColor: 1 },
    { cx: 0.46, cy: 0.68, w: 0.18, h: 0.24, rot: -3, tapeColor: 2 },
    { cx: 0.70, cy: 0.46, w: 0.22, h: 0.30, rot: 5, tapeColor: 3 },
    { cx: 0.83, cy: 0.72, w: 0.16, h: 0.22, rot: -8, tapeColor: 4 },
  ];

  // 胶带花纹色系
  const tapes = [
    { base: '#ffb5b5', stripe: '#ff7a7a' }, // 粉红条纹
    { base: '#ffe27a', stripe: '#ffbf47' }, // 柠檬黄
    { base: '#a9e0b3', stripe: '#6fc884' }, // 薄荷绿
    { base: '#b4d4ff', stripe: '#7fb3ff' }, // 天空蓝
    { base: '#e0b3e8', stripe: '#c283d2' }, // 薰衣草
  ];

  const headerOp = interpolate(frame, [0, 24], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 纸张纤维点
  const paperDots = React.useMemo(() => {
    const arr: Array<{ x: number; y: number; r: number; o: number }> = [];
    for (let i = 0; i < 200; i++) {
      arr.push({
        x: rand(i * 2.7) * width,
        y: rand(i * 3.3) * height,
        r: 0.5 + rand(i * 1.9) * 1.2,
        o: 0.06 + rand(i * 5.1) * 0.08,
      });
    }
    return arr;
  }, [width, height]);

  // 涂鸦贴纸
  type Doodle = {
    x: number;
    y: number;
    type: 'arrow' | 'heart' | 'star' | 'flower' | 'leaf' | 'squiggle';
    rot: number;
    size: number;
    color: string;
    delay: number;
    seed: number;
  };
  const doodles: Doodle[] = React.useMemo(() => {
    const palette = ['#ff6b8e', '#ffa63d', '#66c28a', '#6aa9ff', '#b57dd5', '#e07b4f'];
    const types: Doodle['type'][] = [
      'arrow',
      'heart',
      'star',
      'flower',
      'leaf',
      'squiggle',
    ];
    const picks: Doodle[] = [];
    for (let i = 0; i < 22; i++) {
      picks.push({
        x: 40 + rand(i * 1.3) * (width - 80),
        y: 100 + rand(i * 5.7) * (height - 200),
        type: types[Math.floor(rand(i * 3.1) * types.length)],
        rot: (rand(i * 2.9) - 0.5) * 60,
        size: 22 + rand(i * 7.1) * 28,
        color: palette[Math.floor(rand(i * 9.7) * palette.length)],
        delay: 40 + i * 4,
        seed: i,
      });
    }
    return picks;
  }, [width, height]);

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 50%, #fff6e3 0%, #f7e8c7 65%, #e8d2a0 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 纸张纹理 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {paperDots.map((d, i) => (
          <circle
            key={`pd-${i}`}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill={`rgba(140,100,40,${d.o})`}
          />
        ))}
      </svg>

      {/* 背景手账网格（点阵） */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(160,120,60,0.22) 1px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      {/* 咖啡渍 */}
      <div
        style={{
          position: 'absolute',
          right: 110,
          top: 90,
          width: 140,
          height: 140,
          borderRadius: '50%',
          border: '6px solid rgba(140,90,40,0.18)',
          background:
            'radial-gradient(circle, rgba(180,130,60,0.06) 0%, rgba(160,110,50,0.16) 80%, rgba(140,90,40,0.05) 100%)',
          transform: 'rotate(-15deg)',
          filter: 'blur(0.3px)',
          opacity: headerOp * 0.85,
        }}
      />

      {/* 页面边缘小花边 */}
      <div
        style={{
          position: 'absolute',
          left: 24,
          top: 24,
          right: 24,
          bottom: 24,
          border: '2px dashed rgba(160,120,60,0.35)',
          borderRadius: 12,
          pointerEvents: 'none',
          opacity: headerOp,
        }}
      />

      {/* 顶部标题 + 回形针 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 56,
          textAlign: 'center',
          opacity: headerOp,
          transform: `translateY(${(1 - headerOp) * -16}px)`,
        }}
      >
        <div
          style={{
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", "Segoe Script", cursive',
            fontSize: 62,
            color: '#7a4a2a',
            letterSpacing: 1,
            lineHeight: 1,
            textShadow: '2px 2px 0 rgba(255,240,210,0.7)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
            fontSize: 20,
            color: '#a06a44',
            letterSpacing: 1.5,
          }}
        >
          ~ {subtitle} ~
        </div>
      </div>

      {/* 左上回形针 + 便签 */}
      <div
        style={{
          position: 'absolute',
          left: 52,
          top: 46,
          width: 120,
          height: 80,
          background: 'linear-gradient(145deg, #fff8da 0%, #ffecb4 100%)',
          transform: 'rotate(-8deg)',
          boxShadow:
            '2px 5px 10px rgba(110,70,30,0.25), inset 0 0 0 1px rgba(200,160,100,0.5)',
          opacity: headerOp,
          fontFamily:
            '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
          fontSize: 20,
          color: '#8a5a2a',
          padding: '8px 10px',
          lineHeight: 1.1,
        }}
      >
        today is
        <br />
        <span style={{ fontSize: 26, color: '#c55a33' }}>lovely ♡</span>
        {/* 回形针 */}
        <div
          style={{
            position: 'absolute',
            left: 10,
            top: -14,
            width: 24,
            height: 40,
            borderRadius: 10,
            border: '3px solid #c0c4c9',
            borderBottom: 'none',
            background: 'transparent',
            transform: 'rotate(6deg)',
            boxShadow: '1px 2px 3px rgba(0,0,0,0.2)',
          }}
        />
      </div>

      {/* 右上角日期戳印 */}
      <div
        style={{
          position: 'absolute',
          right: 58,
          top: 54,
          transform: 'rotate(7deg)',
          opacity: headerOp,
        }}
      >
        <div
          style={{
            border: '3px solid #b04a2c',
            borderRadius: 8,
            padding: '8px 16px',
            fontFamily: '"Courier New", monospace',
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: 3,
            color: '#b04a2c',
            textShadow: '0 0 0 currentColor',
            background: 'rgba(255,240,210,0.1)',
            filter: 'blur(0.3px)',
          }}
        >
          ★ {dateStamp} ★
        </div>
      </div>

      {/* 5 张照片 */}
      {photos.map((p, i) => {
        const cx = p.cx * width;
        const cy = p.cy * height;
        const pw = p.w * width;
        const ph = p.h * height;

        const delay = 18 + i * 14;
        // "啪" 地贴上
        const pop = spring({
          frame: frame - delay,
          fps,
          config: { damping: 11, stiffness: 160, mass: 0.9 },
        });
        const popScale = interpolate(pop, [0, 1], [0, 1]);
        const popOp = interpolate(frame - delay, [0, 10], [0, 1], {
          extrapolateRight: 'clamp',
        });

        // 胶带延迟
        const tapeShow = interpolate(frame - delay - 4, [0, 8], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });

        const tape = tapes[p.tapeColor % tapes.length];

        return (
          <div
            key={`photo-${i}`}
            style={{
              position: 'absolute',
              left: cx - pw / 2,
              top: cy - ph / 2,
              width: pw,
              height: ph,
              transform: `rotate(${p.rot}deg) scale(${popScale})`,
              transformOrigin: '50% 50%',
              opacity: popOp,
              zIndex: 10 + i,
            }}
          >
            {/* 阴影 */}
            <div
              style={{
                position: 'absolute',
                inset: -4,
                background:
                  'radial-gradient(ellipse at 50% 60%, rgba(80,40,10,0.25) 0%, transparent 70%)',
                filter: 'blur(10px)',
                borderRadius: 8,
              }}
            />

            {/* 相片底纸（白色带边） */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: '#ffffff',
                padding: 10,
                paddingBottom: 34,
                boxShadow:
                  '0 6px 14px rgba(80,50,20,0.25), inset 0 0 0 1px rgba(180,140,80,0.2)',
                borderRadius: 3,
              }}
            >
              {/* 图像 */}
              <div
                style={{
                  width: '100%',
                  height: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                <Img
                  src={images[i % images.length]}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(0.95) contrast(0.95) brightness(1.02)',
                  }}
                />
                {/* 照片泛黄蒙版 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(ellipse at center, transparent 50%, rgba(220,180,120,0.28) 100%)',
                    mixBlendMode: 'multiply',
                  }}
                />
              </div>
              {/* 手写 caption */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: 10,
                  right: 10,
                  textAlign: 'center',
                  fontFamily:
                    '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
                  fontSize: 16,
                  color: '#74472a',
                  letterSpacing: 0.5,
                }}
              >
                {captions[i % captions.length]}
              </div>
            </div>

            {/* 上方胶带（washi tape） */}
            <div
              style={{
                position: 'absolute',
                left: '20%',
                top: -14,
                width: '60%',
                height: 24,
                opacity: tapeShow,
                transform: `scaleX(${tapeShow}) rotate(${(i % 2 === 0 ? -2 : 3)}deg)`,
                transformOrigin: 'left center',
                background: `repeating-linear-gradient(${45 + i * 22}deg, ${tape.base} 0 8px, ${tape.stripe} 8px 16px)`,
                boxShadow: '0 2px 4px rgba(80,40,10,0.25)',
                border: '1px solid rgba(255,255,255,0.4)',
                maskImage:
                  'linear-gradient(90deg, rgba(0,0,0,0.7) 0%, black 8%, black 92%, rgba(0,0,0,0.7) 100%)',
                WebkitMaskImage:
                  'linear-gradient(90deg, rgba(0,0,0,0.7) 0%, black 8%, black 92%, rgba(0,0,0,0.7) 100%)',
              }}
            />

            {/* 下方第二段小胶带（斜贴角） */}
            {i % 2 === 0 && (
              <div
                style={{
                  position: 'absolute',
                  right: -16,
                  bottom: 18,
                  width: 60,
                  height: 18,
                  opacity: tapeShow,
                  transform: `rotate(${38 - i * 6}deg)`,
                  background: `repeating-linear-gradient(-45deg, ${tape.stripe} 0 6px, ${tape.base} 6px 12px)`,
                  boxShadow: '0 1px 3px rgba(80,40,10,0.3)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              />
            )}
          </div>
        );
      })}

      {/* 散落涂鸦与贴纸 */}
      {doodles.map((d) => {
        const show = interpolate(frame - d.delay, [0, 16], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const bob =
          Math.sin((frame + d.seed * 10) / 24) * (d.type === 'heart' ? 2 : 1);
        return (
          <div
            key={`dd-${d.seed}`}
            style={{
              position: 'absolute',
              left: d.x,
              top: d.y + bob,
              width: d.size,
              height: d.size,
              transform: `rotate(${d.rot}deg) scale(${show})`,
              opacity: show,
              color: d.color,
            }}
          >
            {d.type === 'arrow' && (
              <svg viewBox="0 0 60 30" width={d.size * 2} height={d.size}>
                <path
                  d="M 2 18 Q 16 4 34 14 Q 46 20 54 14"
                  fill="none"
                  stroke={d.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M 48 9 L 56 14 L 49 20"
                  fill="none"
                  stroke={d.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {d.type === 'heart' && (
              <svg viewBox="0 0 24 24" width={d.size} height={d.size}>
                <path
                  d="M12 21s-7-4.5-9.5-9.5C.5 7 4 3 8 4.5 10 5.25 11 6.5 12 8c1-1.5 2-2.75 4-3.5 4-1.5 7.5 2.5 5.5 7C19 16.5 12 21 12 21z"
                  fill={d.color}
                  stroke="rgba(255,255,255,0.7)"
                  strokeWidth="1.5"
                />
              </svg>
            )}
            {d.type === 'star' && (
              <svg viewBox="0 0 24 24" width={d.size} height={d.size}>
                <path
                  d="M12 2l2.6 6.8 7.4.6-5.6 4.9 1.7 7.2L12 17.7 5.9 21.5l1.7-7.2L2 9.4l7.4-.6z"
                  fill="none"
                  stroke={d.color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {d.type === 'flower' && (
              <svg viewBox="0 0 30 30" width={d.size} height={d.size}>
                <g fill={d.color}>
                  <ellipse cx="15" cy="8" rx="4" ry="6" />
                  <ellipse cx="22" cy="15" rx="6" ry="4" />
                  <ellipse cx="15" cy="22" rx="4" ry="6" />
                  <ellipse cx="8" cy="15" rx="6" ry="4" />
                </g>
                <circle cx="15" cy="15" r="3" fill="#fff3b3" />
              </svg>
            )}
            {d.type === 'leaf' && (
              <svg viewBox="0 0 30 30" width={d.size} height={d.size}>
                <path
                  d="M 4 20 Q 8 6 26 6 Q 22 22 4 20 Z"
                  fill={d.color}
                  stroke="rgba(0,0,0,0.1)"
                />
                <path
                  d="M 4 20 Q 14 14 26 6"
                  stroke="rgba(255,255,255,0.6)"
                  strokeWidth="1.2"
                  fill="none"
                />
              </svg>
            )}
            {d.type === 'squiggle' && (
              <svg viewBox="0 0 80 20" width={d.size * 3} height={d.size}>
                <path
                  d="M 2 10 Q 10 2 18 10 T 34 10 T 50 10 T 66 10 T 78 10"
                  fill="none"
                  stroke={d.color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        );
      })}

      {/* 手写箭头 + 签名 */}
      <div
        style={{
          position: 'absolute',
          left: width * 0.08,
          bottom: 70,
          opacity: headerOp * 0.9,
          fontFamily:
            '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
          fontSize: 22,
          color: '#b04a2c',
          transform: 'rotate(-5deg)',
        }}
      >
        ↳ so happy today!
      </div>
      <div
        style={{
          position: 'absolute',
          right: 60,
          bottom: 50,
          opacity: headerOp,
          transform: 'rotate(-8deg)',
        }}
      >
        <div
          style={{
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
            fontSize: 28,
            color: '#c55a33',
            lineHeight: 1,
          }}
        >
          — yuki ♡
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Courier New", monospace',
            fontSize: 11,
            letterSpacing: 2,
            color: '#8a5a2a',
          }}
        >
          PAGE 07 · MMXXVI
        </div>
      </div>

      {/* 纸张褶皱光 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(115deg, transparent 45%, rgba(255,240,200,0.25) 50%, transparent 55%)',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* 暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 60%, rgba(120,80,40,0.3) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------
// #11 ImageCozyWindowSnowfall —— 温馨雪窗台
// 特点：
// · 室内温暖木墙 + 暖黄灯串 + 蓝紫夜空从窗外透入
// · 居中一个木制双开窗，窗格中轮播展示图片（带雪夜滤镜）
// · 窗外飘落雪花（前景/远景两层）
// · 窗台上摆：热可可杯（冒热气）、小盆栽、小熊玩偶、蜡烛
// · 顶部圣诞灯串闪烁 + 窗角结霜
// · 标题放在窗上方，底部"今日日记"手写条
// ---------------------------------------------------------------------
export const ImageCozyWindowSnowfall: React.FC<{
  images?: string[];
  title?: string;
  subtitle?: string;
  secondsPerImage?: number;
}> = ({
  images = DEFAULT_IMAGES_8,
  title = 'Hello, Winter',
  subtitle = 'a little window of warmth',
  secondsPerImage = 2.6,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const width = BASE_W;
  const height = BASE_H;

  // 窗户几何
  const winW = Math.min(width * 0.52, 680);
  const winH = Math.min(height * 0.58, 440);
  const winX = (width - winW) / 2;
  const winY = height * 0.22;
  const sashGap = 8;

  // 当前图片索引 + 淡入
  const per = Math.max(1, Math.round(secondsPerImage * fps));
  const idx = Math.floor(frame / per) % images.length;
  const nextIdx = (idx + 1) % images.length;
  const localT = (frame % per) / per;
  const switchZone = 0.12;
  const switching =
    localT > 1 - switchZone ? (localT - (1 - switchZone)) / switchZone : 0;

  const headerOp = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 前景雪花
  const snowFront = React.useMemo(() => {
    const arr: Array<{
      x: number;
      ySeed: number;
      r: number;
      speed: number;
      sway: number;
      seed: number;
    }> = [];
    for (let i = 0; i < 60; i++) {
      arr.push({
        x: rand(i * 1.3) * width,
        ySeed: rand(i * 3.7) * height,
        r: 2 + rand(i * 5.1) * 3.5,
        speed: 0.8 + rand(i * 7.7) * 1.4,
        sway: 20 + rand(i * 9.1) * 40,
        seed: i,
      });
    }
    return arr;
  }, [width, height]);

  // 窗内雪花（更柔和）
  const snowInside = React.useMemo(() => {
    const arr: Array<{
      x: number;
      ySeed: number;
      r: number;
      speed: number;
      sway: number;
      seed: number;
    }> = [];
    for (let i = 0; i < 50; i++) {
      arr.push({
        x: rand(i * 2.7) * winW,
        ySeed: rand(i * 4.3) * winH,
        r: 1 + rand(i * 6.1) * 2,
        speed: 0.5 + rand(i * 8.7) * 0.8,
        sway: 10 + rand(i * 3.1) * 20,
        seed: i,
      });
    }
    return arr;
  }, [winW, winH]);

  // 顶部圣诞灯串（光点）
  const lights = React.useMemo(() => {
    const arr: Array<{ x: number; hue: number; seed: number }> = [];
    const count = 14;
    for (let i = 0; i < count; i++) {
      arr.push({
        x: (i + 0.5) / count,
        hue: [0, 35, 60, 180, 280, 320][i % 6],
        seed: i,
      });
    }
    return arr;
  }, []);

  // 灯串波动曲线
  const wireY = (tx: number) => 68 + Math.sin(tx * Math.PI * 2 - 0.3) * 26;

  // 窗内一个图片单元的渲染
  const renderWindowImage = (imgIdx: number, alpha: number) => {
    // 3-frame reveal for photo: scale + pan
    const kb = interpolate(frame, [0, fps * 12], [1, 1.08]);
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          opacity: alpha,
        }}
      >
        <Img
          src={images[imgIdx % images.length]}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${kb})`,
            filter:
              'saturate(0.82) contrast(0.92) brightness(0.78) hue-rotate(-6deg)',
          }}
        />
        {/* 蓝紫夜色 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(30,50,100,0.35) 0%, rgba(20,30,70,0.2) 40%, rgba(10,20,50,0.35) 100%)',
            mixBlendMode: 'multiply',
          }}
        />
      </div>
    );
  };

  return (
    <AbsoluteFill
      style={{
        background:
          'linear-gradient(180deg, #3a2220 0%, #57322a 40%, #70412f 80%, #4c291f 100%)',
        overflow: 'hidden',
      }}
    >
      <ScaledStage>
      {/* 木墙纹理（条纹） */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(90deg, rgba(60,30,15,0.3) 0 2px, transparent 2px 110px), repeating-linear-gradient(0deg, rgba(30,15,10,0.22) 0 1px, transparent 1px 180px)',
          pointerEvents: 'none',
        }}
      />
      {/* 木纹亮斑 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at 50% 30%, rgba(255,200,130,0.25) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />

      {/* 顶部灯串 */}
      <svg
        width={width}
        height={140}
        style={{ position: 'absolute', left: 0, top: 0 }}
      >
        <path
          d={`M 0 ${wireY(0)} Q ${width / 2} ${wireY(0.5) + 30} ${width} ${wireY(1)}`}
          fill="none"
          stroke="#1e0f08"
          strokeWidth="2"
        />
      </svg>
      {lights.map((l) => {
        const lx = l.x * width;
        const ly = wireY(l.x) + Math.sin(l.x * Math.PI * 2 - 0.3) * 0 + 10;
        const flick = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(frame / 5 + l.seed));
        const color = `hsl(${l.hue},90%,70%)`;
        const glow = `hsla(${l.hue},95%,65%,${0.6 * flick})`;
        return (
          <div
            key={`light-${l.seed}`}
            style={{
              position: 'absolute',
              left: lx - 9,
              top: ly - 6,
              width: 18,
              height: 22,
              opacity: headerOp,
              pointerEvents: 'none',
            }}
          >
            {/* 光晕 */}
            <div
              style={{
                position: 'absolute',
                left: -28,
                top: -28,
                width: 74,
                height: 74,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${glow} 0%, transparent 70%)`,
                filter: 'blur(4px)',
              }}
            />
            {/* 灯泡 */}
            <div
              style={{
                width: 16,
                height: 20,
                borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                background: `radial-gradient(circle at 35% 30%, #ffffff, ${color} 55%, ${color}aa 100%)`,
                boxShadow: `0 0 14px ${color}, 0 0 26px ${glow}`,
              }}
            />
            <div
              style={{
                width: 8,
                height: 3,
                background: '#2a1a10',
                margin: '0 auto',
                borderRadius: 1,
              }}
            />
          </div>
        );
      })}

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 150,
          textAlign: 'center',
          opacity: headerOp,
          transform: `translateY(${(1 - headerOp) * -14}px)`,
        }}
      >
        <div
          style={{
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", "Segoe Script", cursive',
            fontSize: 58,
            color: '#ffe7bd',
            letterSpacing: 2,
            lineHeight: 1,
            textShadow:
              '0 0 18px rgba(255,180,120,0.45), 2px 3px 0 rgba(80,30,20,0.4)',
          }}
        >
          ❄ {title} ❄
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
            fontSize: 20,
            color: '#f5c990',
            letterSpacing: 2,
          }}
        >
          ~ {subtitle} ~
        </div>
      </div>

      {/* 窗户外框阴影 */}
      <div
        style={{
          position: 'absolute',
          left: winX - 10,
          top: winY - 10,
          width: winW + 20,
          height: winH + 20,
          background: 'rgba(0,0,0,0.45)',
          filter: 'blur(20px)',
          borderRadius: 4,
        }}
      />

      {/* 窗户外框（木制） */}
      <div
        style={{
          position: 'absolute',
          left: winX,
          top: winY,
          width: winW,
          height: winH,
          background:
            'linear-gradient(145deg, #8c5a38 0%, #6b3e22 40%, #8c5a38 100%)',
          border: '10px solid #4a2a18',
          boxShadow:
            'inset 0 0 0 3px rgba(255,220,180,0.15), inset 0 0 40px rgba(0,0,0,0.5), 0 18px 40px rgba(0,0,0,0.55)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {/* 窗内夜空 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 50% 30%, #2d406b 0%, #1a2344 55%, #0e1530 100%)',
          }}
        />

        {/* 远处月亮 */}
        <div
          style={{
            position: 'absolute',
            left: winW * 0.68,
            top: winH * 0.12,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 35% 35%, #fff6d4 0%, #ffe4a3 55%, rgba(255,228,163,0) 75%)',
            boxShadow: '0 0 40px rgba(255,230,180,0.5)',
            opacity: 0.9,
          }}
        />

        {/* 窗内图片（当前 + 下一张过渡） */}
        {renderWindowImage(idx, 1 - switching)}
        {switching > 0 && renderWindowImage(nextIdx, switching)}

        {/* 窗内前景雪花 */}
        {snowInside.map((s) => {
          const y =
            ((s.ySeed + frame * s.speed * 2) % (winH + 40)) - 20;
          const x =
            s.x +
            Math.sin((frame + s.seed * 20) / 40) * (s.sway * 0.4);
          return (
            <div
              key={`si-${s.seed}`}
              style={{
                position: 'absolute',
                left: x,
                top: y,
                width: s.r * 2,
                height: s.r * 2,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)',
                filter: 'blur(0.4px)',
              }}
            />
          );
        })}

        {/* 窗玻璃反光 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(120deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.18) 40%, rgba(255,255,255,0) 55%)',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
          }}
        />

        {/* 窗角结霜 */}
        {[
          { x: 0, y: 0 },
          { x: winW, y: 0 },
          { x: 0, y: winH },
          { x: winW, y: winH },
        ].map((c, ci) => (
          <div
            key={`frost-${ci}`}
            style={{
              position: 'absolute',
              left: c.x - 80,
              top: c.y - 80,
              width: 160,
              height: 160,
              background:
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.55) 0%, rgba(210,230,255,0.25) 35%, rgba(255,255,255,0) 65%)',
              filter: 'blur(2px)',
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* 十字窗棂（垂直 + 水平） */}
        <div
          style={{
            position: 'absolute',
            left: winW / 2 - sashGap / 2,
            top: 0,
            width: sashGap,
            height: winH,
            background:
              'linear-gradient(90deg, #3b2212 0%, #8c5a38 50%, #3b2212 100%)',
            boxShadow: '0 0 6px rgba(0,0,0,0.6)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: winH / 2 - sashGap / 2,
            width: winW,
            height: sashGap,
            background:
              'linear-gradient(180deg, #3b2212 0%, #8c5a38 50%, #3b2212 100%)',
            boxShadow: '0 0 6px rgba(0,0,0,0.6)',
          }}
        />

        {/* 图片轮播指示点 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 8,
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            opacity: 0.85,
          }}
        >
          {images.map((_, di) => (
            <div
              key={`dot-${di}`}
              style={{
                width: di === idx ? 16 : 6,
                height: 6,
                borderRadius: 3,
                background:
                  di === idx
                    ? 'rgba(255,220,160,0.95)'
                    : 'rgba(255,220,160,0.35)',
                transition: 'all 0.3s',
              }}
            />
          ))}
        </div>
      </div>

      {/* 窗台（木板） */}
      <div
        style={{
          position: 'absolute',
          left: winX - 40,
          top: winY + winH - 4,
          width: winW + 80,
          height: 28,
          background:
            'linear-gradient(180deg, #3a1f10 0%, #5a3520 50%, #2b1608 100%)',
          boxShadow:
            '0 10px 20px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,220,180,0.15)',
          borderRadius: 3,
        }}
      />

      {/* 窗台上: 热可可杯 */}
      <div
        style={{
          position: 'absolute',
          left: winX + 20,
          top: winY + winH - 60,
          width: 64,
          height: 60,
          zIndex: 20,
        }}
      >
        {/* 热气 */}
        <svg
          width="80"
          height="80"
          style={{
            position: 'absolute',
            left: -10,
            top: -60,
            opacity: 0.85,
            pointerEvents: 'none',
          }}
        >
          {[0, 1, 2].map((si) => {
            const t = ((frame * 1.5 + si * 20) / 60) % 1;
            const op = t < 0.2 ? t * 5 : t > 0.8 ? (1 - t) * 5 : 1;
            const sway = Math.sin((frame + si * 25) / 14) * 6;
            return (
              <path
                key={`steam-${si}`}
                d={`M ${40 + si * 8 + sway} ${70 - t * 60} Q ${36 + si * 8 + sway - 6} ${50 - t * 60} ${40 + si * 8 + sway} ${30 - t * 60}`}
                stroke="rgba(255,240,220,0.7)"
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                opacity={op * 0.7}
                filter="blur(1.5px)"
              />
            );
          })}
        </svg>
        {/* 杯身 */}
        <div
          style={{
            position: 'absolute',
            left: 6,
            bottom: 0,
            width: 52,
            height: 50,
            background: 'linear-gradient(180deg, #f5c58d 0%, #d88a52 100%)',
            borderRadius: '6px 6px 10px 10px',
            border: '2px solid #7a3e1c',
            boxShadow:
              '0 6px 8px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,240,210,0.25)',
          }}
        >
          {/* 可可液面 */}
          <div
            style={{
              position: 'absolute',
              top: 3,
              left: 3,
              right: 3,
              height: 8,
              background: 'linear-gradient(180deg, #4a2515 0%, #3a1c0e 100%)',
              borderRadius: 3,
              boxShadow:
                'inset 0 0 0 1px rgba(255,230,180,0.4), 0 0 8px rgba(90,40,20,0.6)',
            }}
          />
          {/* 棉花糖 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 14,
              width: 10,
              height: 7,
              background: '#fff4e8',
              borderRadius: 3,
              boxShadow: '0 1px 2px rgba(100,60,30,0.4)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 1,
              left: 26,
              width: 10,
              height: 6,
              background: '#ffe5d2',
              borderRadius: 3,
            }}
          />
          {/* 爱心图案 */}
          <div
            style={{
              position: 'absolute',
              top: 22,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: 16,
              color: '#ffe2c0',
            }}
          >
            ♡
          </div>
        </div>
        {/* 杯柄 */}
        <div
          style={{
            position: 'absolute',
            right: -2,
            bottom: 12,
            width: 14,
            height: 22,
            border: '4px solid #7a3e1c',
            borderLeft: 'none',
            borderRadius: '0 10px 10px 0',
            background: 'transparent',
          }}
        />
      </div>

      {/* 窗台上: 小熊玩偶 */}
      <div
        style={{
          position: 'absolute',
          left: winX + winW - 70,
          top: winY + winH - 52,
          width: 52,
          height: 56,
          zIndex: 20,
        }}
      >
        {/* 头 */}
        <div
          style={{
            position: 'absolute',
            left: 10,
            top: 0,
            width: 34,
            height: 30,
            background: 'linear-gradient(180deg, #b9865a 0%, #95623d 100%)',
            borderRadius: '50%',
            border: '2px solid #5c3518',
          }}
        >
          {/* 耳朵 */}
          <div
            style={{
              position: 'absolute',
              left: -4,
              top: -4,
              width: 14,
              height: 14,
              background: '#a47448',
              borderRadius: '50%',
              border: '2px solid #5c3518',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: -4,
              top: -4,
              width: 14,
              height: 14,
              background: '#a47448',
              borderRadius: '50%',
              border: '2px solid #5c3518',
            }}
          />
          {/* 脸 */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '55%',
              transform: 'translate(-50%, -50%)',
              width: 20,
              height: 12,
              background: '#e5c08e',
              borderRadius: '50%',
            }}
          />
          {/* 眼 */}
          <div
            style={{
              position: 'absolute',
              left: 9,
              top: 10,
              width: 3,
              height: 3,
              background: '#2a1406',
              borderRadius: '50%',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 9,
              top: 10,
              width: 3,
              height: 3,
              background: '#2a1406',
              borderRadius: '50%',
            }}
          />
          {/* 鼻 */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 16,
              transform: 'translateX(-50%)',
              width: 3,
              height: 2,
              background: '#2a1406',
              borderRadius: 2,
            }}
          />
          {/* 嘴 */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 19,
              transform: 'translateX(-50%)',
              fontSize: 6,
              color: '#2a1406',
            }}
          >
            ‿
          </div>
        </div>
        {/* 身体 */}
        <div
          style={{
            position: 'absolute',
            left: 6,
            top: 24,
            width: 40,
            height: 30,
            background: 'linear-gradient(180deg, #a47448 0%, #7a5130 100%)',
            borderRadius: '50% 50% 30% 30%',
            border: '2px solid #5c3518',
          }}
        />
        {/* 围巾（红色） */}
        <div
          style={{
            position: 'absolute',
            left: 2,
            top: 22,
            width: 48,
            height: 9,
            background: 'repeating-linear-gradient(90deg, #c44a3a 0 6px, #a33326 6px 12px)',
            borderRadius: 4,
            border: '1.5px solid #5a1a13',
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }}
        />
      </div>

      {/* 窗台上: 小盆栽 */}
      <div
        style={{
          position: 'absolute',
          left: winX + winW * 0.38,
          top: winY + winH - 40,
          width: 40,
          height: 44,
          zIndex: 20,
        }}
      >
        {/* 叶子 */}
        <div
          style={{
            position: 'absolute',
            left: 8,
            top: 0,
            width: 24,
            height: 22,
            background:
              'radial-gradient(circle at 30% 30%, #8bc47a 0%, #3e7a3c 70%, #275828 100%)',
            borderRadius: '50% 50% 40% 40%',
            transform: 'rotate(-10deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 4,
            top: 6,
            width: 12,
            height: 14,
            background: '#5fa654',
            borderRadius: '60% 40% 50% 50%',
            transform: 'rotate(-30deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: 2,
            top: 4,
            width: 12,
            height: 14,
            background: '#5fa654',
            borderRadius: '40% 60% 50% 50%',
            transform: 'rotate(30deg)',
          }}
        />
        {/* 花盆 */}
        <div
          style={{
            position: 'absolute',
            left: 4,
            bottom: 0,
            width: 32,
            height: 22,
            background: 'linear-gradient(180deg, #c75a3c 0%, #8a3018 100%)',
            borderRadius: '3px 3px 8px 8px',
            border: '2px solid #3e160b',
            clipPath: 'polygon(0 0, 100% 0, 92% 100%, 8% 100%)',
          }}
        />
        {/* 盆边 */}
        <div
          style={{
            position: 'absolute',
            left: 2,
            bottom: 18,
            width: 36,
            height: 6,
            background: '#8a3018',
            border: '2px solid #3e160b',
            borderRadius: 3,
          }}
        />
      </div>

      {/* 前景雪花（在窗前飘） */}
      {snowFront.map((s) => {
        const y = ((s.ySeed + frame * s.speed * 3) % (height + 60)) - 30;
        const x =
          s.x + Math.sin((frame + s.seed * 30) / 50) * s.sway;
        return (
          <div
            key={`sf-${s.seed}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: s.r * 2,
              height: s.r * 2,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 80%)',
              filter: 'blur(0.5px)',
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* 底部温馨签名条（手写） */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 22,
          textAlign: 'center',
          opacity: headerOp,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '8px 22px',
            background: 'rgba(255,240,220,0.15)',
            border: '1.5px dashed rgba(255,220,170,0.55)',
            borderRadius: 20,
            fontFamily:
              '"Caveat", "Patrick Hand", "Comic Sans MS", cursive',
            fontSize: 18,
            letterSpacing: 3,
            color: '#ffe0b8',
            backdropFilter: 'blur(4px)',
          }}
        >
          ✧ stay warm, little one · photo {String(idx + 1).padStart(2, '0')}/
          {String(images.length).padStart(2, '0')} ✧
        </div>
      </div>

      {/* 整体暖色暗角 */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(20,10,5,0.7) 100%)',
          pointerEvents: 'none',
        }}
      />
      </ScaledStage>
    </AbsoluteFill>
  );
};
